import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import AdminContext from "../../../StateContext/Application";
import { apiurl } from "../../../../../path";
import { useLanguage } from "../../../../../lang/langcontext/LanguageContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import GooglePagination from "../../../styles/GooglePagination";
import { useNavigate } from "react-router-dom";
import { formatDateTime, formatDate } from "../../../../../utils/DateFormat";

/* ------------------------------------------------------------------ */
/*  constants / pure helpers                                           */
/* ------------------------------------------------------------------ */
const SPECIAL_DISTRICTS = ["जालना", "नांदेड"];
const SPECIAL_STAGES = ["Approvance1", "Approvance3", "Approvance7"]; // Jalna/Nanded 2515
const LEGACY_COLUMNS = [
  // used only when no flow is configured for the district
  { stage: "Approvance1", label: "Department Auditor" },
  { stage: "Approvance2", label: "Department HOD(EE)" },
  { stage: "Approvance3", label: "FD Auditor" },
  { stage: "Approvance4", label: "FD AAO" },
  { stage: "Approvance6", label: "Deputy CAFO" },
  { stage: "Approvance7", label: "CAFO" },
];
const ROLE_LABELS = { "Finance Department(Auditor)": "FD Auditor" };
const roleLabel = (r) => ROLE_LABELS[r] || r;
const baseDistrict = (d) => String(d || "").split("-")[0].trim();
const isSpecialRow = (row) =>
  SPECIAL_DISTRICTS.includes(baseDistrict(row.District)) &&
  String(row.BudgetID || "").startsWith("2515");
const inDistrict = (itemDistrict, district) =>
  itemDistrict === district || itemDistrict.startsWith(`${district}-`);

const selectBoxStyle = {
  width: "100%",
  height: "40px",
  padding: "6px 10px",
  border: "1px solid #ccc",
  borderRadius: "8px",
  fontSize: "14px",
  backgroundColor: "#f1f6fc",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const footerTitleStyle = { width: "100%", borderCollapse: "collapse", marginTop: "8px" };
const footerTitleTdStyle = {
  border: "1px solid #555", padding: "6px", textAlign: "center", fontSize: "12px", fontWeight: "bold",
};
const remarksTableStyle = { width: "100%", borderCollapse: "collapse" };
const remarksTableTdStyle = {
  border: "1px solid #555", padding: "8px", fontSize: "11px", lineHeight: "18px", textAlign: "right",
};
const instructionTableStyle = { width: "100%", borderCollapse: "collapse", marginTop: "-1px" };
const instructionTableTdStyle = {
  border: "1px solid #555", padding: "10px", fontSize: "11px", lineHeight: "18px", textAlign: "justify",
};

/* checkbox dropdown used for District + Taluka */
const MultiSelectBox = ({ placeholder, allLabel, options, selected, onToggle, onToggleAll, allSelected, open, setOpen }) => (
  <div style={{ position: "relative", width: "150px", flexShrink: 0 }}>
    <div
      onClick={() => setOpen(!open)}
      style={{ ...selectBoxStyle, boxSizing: "border-box", padding: "9px 10px", cursor: "pointer" }}
    >
      {selected.length > 0 ? selected.join(", ") : placeholder}
    </div>

    {open && (
      <div
        style={{
          position: "absolute", top: "44px", left: 0, width: "150px", background: "#fff",
          border: "1px solid #ccc", borderRadius: "8px", boxShadow: "0 4px 10px rgba(0,0,0,0.12)", zIndex: 1000,
        }}
      >
        <label
          style={{
            display: "flex", alignItems: "center", padding: "8px",
            borderBottom: "1px solid #eee", cursor: "pointer", fontSize: "13px",
          }}
        >
          <input type="checkbox" checked={allSelected} onChange={onToggleAll} style={{ marginRight: "6px" }} />
          {allLabel}
        </label>

        <div style={{ maxHeight: "150px", overflowY: "auto" }}>
          {options.map((opt, index) => (
            <label
              key={index}
              style={{ display: "flex", alignItems: "center", padding: "7px 8px", cursor: "pointer", fontSize: "13px" }}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => onToggle(opt)}
                onClick={(e) => e.stopPropagation()}
                style={{ marginRight: "6px" }}
              />
              {opt}
            </label>
          ))}
        </div>

        <div style={{ padding: "6px", textAlign: "right", borderTop: "1px solid #eee" }}>
          <button
            onClick={() => setOpen(false)}
            style={{
              padding: "4px 10px", fontSize: "12px", border: "none", borderRadius: "6px",
              background: "#0d6efd", color: "#fff", cursor: "pointer",
            }}
          >
            OK
          </button>
        </div>
      </div>
    )}
  </div>
);

/* ✓ / ✕ / - cell */
const renderTick = (value) => {
  if (value !== "true" && value !== "false") return "-";
  const ok = value === "true";
  return (
    <div
      className="m-auto"
      style={{
        width: "20px", height: "20px", borderRadius: "6px",
        backgroundColor: ok ? "#16C60C" : "#dc2626",
        display: "flex", justifyContent: "center", alignItems: "center",
        fontSize: "13px", color: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {ok ? "✓" : "✕"}
    </div>
  );
};

const formatFileName = (file) => {
  if (!file) return "";
  const dotIndex = file.lastIndexOf(".");
  const extension = dotIndex !== -1 ? file.substring(dotIndex) : "";
  const lastThree = dotIndex !== -1 ? file.substring(dotIndex - 4, dotIndex) : "";
  return `${file.substring(0, 10)}........${lastThree}${extension}`;
};

/* ------------------------------------------------------------------ */
const ViewDemandGeneration = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const context = useContext(AdminContext);
  const { GetBudgetHeadInfo, TechnicalOutwards, Usermenu, Getyears } = context;

  /* ---------- state ---------- */
  const [departments, setDepartments] = useState([]);
  const [flows, setFlows] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");

  const [currentUserID, setCurrentUserID] = useState();
  const [progressData, setProgressData] = useState([]);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [technicalInwards, setTechnicalInwards] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [proposallist, setProposalList] = useState([]);
  const [SelectedTaluka, setSelectedTaluka] = useState([]);
  const [selectedWorkOrderType, setSelectedWorkOrderType] = useState("");
  const [selectedHeadCode, setSelectedHeadCode] = useState("");
  const [openTaluka, setOpenTaluka] = useState(false);

  const [UserRole, setUserRole] = useState("");
  const [AllDistrict, setAllDistrict] = useState([]);
  const [SelectedDistrict, setSelectedDistrict] = useState([]);
  const [statusofData1, setstatusofData1] = useState("");
  const [RoleID, setroleID] = useState(null);
  const [typeofData, settypeofData] = useState("District");
  const [openDistrict, setOpenDistrict] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [exportingPDF, setExportingPDF] = useState(false);
  const [loading, setloading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [approvedYear, setApprovedYear] = useState("");
  const [showspecific, setShowSpecific] = useState(0);
  const [selectedBillData, setselectedBillData] = useState({});
  const [selectedDemandID, setSelectedDemandID] = useState();
  const [loginDistrict, setLoginDistrict] = useState("");
  const [totalBillsCount, setTotalBillsCount] = useState(0);
  const [fetchSpecificDemandDeductionDetail, setfetchSpecficDemandDeductionDetail] = useState([]);
  const [exportingBillSlipPDF, setExportingBillSlipPDF] = useState(false);

  const billSlipRef = useRef(null);

  /* ---------- derived: department-scoped data ---------- */
  // Every list / dropdown / filter below starts from THIS array, so changing the
  // department re-runs everything (it is a dependency of the filter effect).
  const deptScopedBills = useMemo(() => {
    const all = Array.isArray(technicalInwards) ? technicalInwards : [];
    if (!selectedDepartmentId) return all;
    return all.filter(
      (item) => String(item.DepartmentAccessID ?? "") === String(selectedDepartmentId),
    );
  }, [technicalInwards, selectedDepartmentId]);

  /* ---------- derived: flow helpers ---------- */
  const isChecker =
    RoleID === 80 ||
    RoleID === 81 ||
    ((RoleID === 40 || RoleID === 41) &&
      (SelectedDistrict.includes("जालना") || SelectedDistrict.includes("नांदेड"))) ||
    ((loginDistrict === "जालना" || loginDistrict === "नांदेड") &&
      selectedHeadCode?.startsWith("2515"));

  // flow (role sequence + stages) that applies to one bill row
  const flowForRow = (row) => {
    const d = baseDistrict(row.District);
    const dep = row.DepartmentAccessID == null ? null : String(row.DepartmentAccessID);
    const cands = flows.filter((f) => f.district === d);
    return (dep && cands.find((f) => f.departmentIds.includes(dep))) || cands[0] || null;
  };

  const rowStages = (row) => {
    if (isSpecialRow(row)) return SPECIAL_STAGES;
    const f = flowForRow(row);
    return f ? f.stages : LEGACY_COLUMNS.map((c) => c.stage);
  };

  const isStageApplicable = (row, stage) => rowStages(row).includes(stage);

  const isFullyApproved = (row) =>
    rowStages(row).every((s) => row[s] === "true") && row.Approvance9 === "true";

  // "Approved by" columns = union of the stages of the flows being shown
  const outwardColumns = useMemo(() => {
    const relevant = flows.filter(
      (f) =>
        (!selectedDepartmentId || f.departmentIds.includes(String(selectedDepartmentId))) &&
        (SelectedDistrict.length === 0 || SelectedDistrict.includes(f.district)),
    );
    let cols;
    if (!relevant.length) {
      cols = LEGACY_COLUMNS;
    } else {
      const map = {};
      relevant.forEach((f) =>
        f.stages.forEach((st, i) => {
          (map[st] = map[st] || new Set()).add(roleLabel(f.sequence[i]));
        }),
      );
      cols = Object.keys(map)
        .sort()
        .map((st) => ({ stage: st, label: [...map[st]].join(" / ") }));
    }
    return isChecker ? cols.filter((c) => SPECIAL_STAGES.includes(c.stage)) : cols;
  }, [flows, selectedDepartmentId, SelectedDistrict, isChecker]);

  /* ---------- pagination ---------- */
  const billsPerPage = showAll ? filteredBills.length : 10;
  const totalPages = Math.ceil(filteredBills?.length / billsPerPage);
  const indexOfLastBill = currentPage * billsPerPage;
  const indexOfFirstBill = indexOfLastBill - billsPerPage;
  const currentBills = filteredBills?.slice(indexOfFirstBill, indexOfLastBill);
  const displayedGRS = exportingPDF ? filteredBills : currentBills;

  /* ---------- data loading ---------- */
  const getTechnicalInwardsList = async () => {
    const data1 = await TechnicalOutwards("Bill");
    const {
      status,
      message,
      Tabletype,
      departments: deps = [],
      flows: fl = [],
      defaultDepartmentId = "",
    } = data1;

    if (status) {
      setTechnicalInwards(message);
      setProposalList(message);
      setUserRole(Tabletype);
      setDepartments(deps);
      setFlows(fl);
      setSelectedDepartmentId(deps.length ? defaultDepartmentId : ""); // बांधकाम first
    } else {
      alert(message);
    }
  };

  const getmenu = async () => {
    try {
      const data = await Usermenu();
      if (!data || typeof data !== "object") {
        console.error("Invalid data:", data);
        return;
      }
      setroleID(data?.Data?.RoleID);
      setLoginDistrict(data?.Data.District);
      setCurrentUserID(data.Data.ID);
    } catch (error) {
      console.error("Error in AdminNavbar getmenu:", error);
    }
  };

  const FetchGramPanachayatInfo = async () => {
    try {
      const res = await GetBudgetHeadInfo("null", "AllDistrict");
      setAllDistrict(res);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getTechnicalInwardsList();
    getmenu();
    FetchGramPanachayatInfo();
  }, []);

  /* ---------- district / taluka option lists ---------- */
  const allDistricts = [
    ...new Set(AllDistrict?.map((d) => baseDistrict(d.District_Marathi || ""))),
  ].filter(Boolean);
  const isAllSelected = SelectedDistrict.length === allDistricts.length;

  const toggleAllDistricts = () => setSelectedDistrict(isAllSelected ? [] : allDistricts);
  const handleDistrictToggle = (district) =>
    setSelectedDistrict((prev) =>
      prev.includes(district) ? prev.filter((d) => d !== district) : [...prev, district],
    );

  // Talukas depend on the selected department AND the selected district(s)
  const allTalukas = [
    ...new Set(
      deptScopedBills
        .filter((item) => {
          if (SelectedDistrict.length === 0) return true;
          const itemDistrict = item.District || item.District_Marathi || "";
          return SelectedDistrict.some((district) => inDistrict(itemDistrict, district));
        })
        .map((item) => item.ulb || item.Taluka || item.ULBNameMarathi),
    ),
  ].filter(Boolean);
  const isAllTalukaSelected = allTalukas.length > 0 && SelectedTaluka.length === allTalukas.length;

  const toggleAllTalukas = () => setSelectedTaluka(isAllTalukaSelected ? [] : allTalukas);
  const handleTalukaToggle = (taluka) =>
    setSelectedTaluka((prev) =>
      prev.includes(taluka) ? prev.filter((x) => x !== taluka) : [...prev, taluka],
    );

  useEffect(() => {
    setSelectedTaluka([]);
  }, [SelectedDistrict]);

  // changing department clears the dependent filters (their options change)
  const handleDepartmentChange = (e) => {
    setSelectedDepartmentId(e.target.value);
    setSelectedTaluka([]);
    setSelectedHeadCode("");
    setApprovedYear("");
    setSelectedWorkOrderType("");
    setCurrentPage(1);
  };

  /* ---------- THE FILTER ---------- */
  useEffect(() => {
    let filtered = [...deptScopedBills]; // already limited to the chosen department

    if (approvedYear) {
      filtered = filtered.filter((item) => String(item.WorkApprovedYear) === String(approvedYear));
    }

    if (typeofData === "District" && RoleID === 29) {
      filtered = filtered.filter(
        (item) => item.BudgetID === "3054-2419(Roads and bridge repair maintenance program)",
      );
    } else if (typeofData === "State" && RoleID === 29) {
      filtered = filtered.filter((item) => item.BudgetID === "2515-1238(MLA Representative)");
    }

    if (SelectedDistrict?.length > 0) {
      filtered = filtered.filter((item) =>
        SelectedDistrict.some((district) =>
          inDistrict(item.District || item.District_Marathi || "", district),
        ),
      );
    }

    if (SelectedTaluka?.length > 0) {
      filtered = filtered.filter((item) =>
        SelectedTaluka.includes(item.ulb || item.ULBNameMarathi || item.Taluka),
      );
    }

    if (selectedWorkOrderType) {
      filtered = filtered.filter(
        (item) => (item.WorkOrderType || item.workOrderType) === selectedWorkOrderType,
      );
    }

    if (selectedHeadCode) {
      filtered = filtered.filter((item) => item.BudgetID === selectedHeadCode);
    }

    if (statusofData1) {
      if (statusofData1 === "false") {
        // Pending
        filtered = filtered.filter(
          (item) =>
            item.Status !== "Rejected" && item.Status !== "SendBack" && !isFullyApproved(item),
        );
      } else if (statusofData1 === "Approved") {
        filtered = filtered.filter((item) => isFullyApproved(item));
      } else if (statusofData1 === "Rejected") {
        filtered = filtered.filter((item) => item.Status === "Rejected");
      } else if (statusofData1 === "SendBack") {
        filtered = filtered.filter((item) => item.Status === "SendBack");
      }
    }

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((bill) =>
        Object.values(bill).some((val) => String(val ?? "").toLowerCase().includes(lower)),
      );
    }

    if (fromDate || toDate) {
      filtered = filtered.filter((item) => {
        if (!item.Date) return false;
        const uploadDate = new Date(item.Date);
        uploadDate.setHours(0, 0, 0, 0);
        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : null;
        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(23, 59, 59, 999);
        if (from && uploadDate < from) return false;
        if (to && uploadDate > to) return false;
        return true;
      });
    }

    // RoleID 80/81 -> always 2515 records only
    if (RoleID === 80 || RoleID === 81) {
      filtered = filtered.filter((item) => item.BudgetID?.startsWith("2515"));
    }

    setTotalBillsCount(filtered.length);

    // hide 2515 Jalna/Nanded records by default for District login
    if (
      (loginDistrict === "जालना" || loginDistrict === "नांदेड") &&
      RoleID !== 40 &&
      RoleID !== 41 &&
      RoleID !== 80 &&
      RoleID !== 81 &&
      SelectedDistrict.length === 0 &&
      !selectedHeadCode
    ) {
      filtered = filtered.filter((item) => {
        const is2515 = item.BudgetID?.startsWith("2515");
        const itemDistrict = item.District || item.District_Marathi || "";
        const isJalnaNanded = SPECIAL_DISTRICTS.some((d) => inDistrict(itemDistrict, d));
        return !(is2515 && isJalnaNanded);
      });
    }

    // hide Jalna/Nanded by default for State 40/41 login
    if ((RoleID === 40 || RoleID === 41) && SelectedDistrict.length === 0) {
      filtered = filtered.filter((item) => {
        const itemDistrict = item.District || item.District_Marathi || "";
        return !SPECIAL_DISTRICTS.some((d) => inDistrict(itemDistrict, d));
      });
    }

    setFilteredBills(filtered);
    setCurrentPage(1);
  }, [
    deptScopedBills,
    flows,
    statusofData1,
    searchTerm,
    SelectedDistrict,
    typeofData,
    fromDate,
    toDate,
    approvedYear,
    SelectedTaluka,
    selectedWorkOrderType,
    selectedHeadCode,
    RoleID,
    loginDistrict,
  ]);

  /* ---------- totals ---------- */
  const sumOf = (list, key) =>
    list?.reduce((sum, item) => {
      const amount = Number(item?.[key]);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0) || 0;

  const totalBillamt = sumOf(filteredBills, "BillAmount");
  const totalpayableamt = sumOf(filteredBills, "NetPayableAmount");
  const totaldeductionamt = sumOf(filteredBills, "DeductionAmount");
  const pagetotalBillamt = sumOf(currentBills, "BillAmount");
  const pagetotalpayableamt = sumOf(currentBills, "NetPayableAmount");
  const pagetotaldeductionamt = sumOf(currentBills, "DeductionAmount");

  /* ---------- Excel export ---------- */
  const exportTableByClassToExcel = async (bills, filename = "Bills_Outward.xlsx") => {
    if (!bills || bills.length === 0) {
      alert("No data available");
      return;
    }

    setloading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      const getStatus = (val) => (val === "true" ? "✓" : val === "false" ? "✕" : "-");
      const actionBy = (p) =>
        p.Status === "SendBack"
          ? `Send Back By - ${p.ActionBy === "NULL" ? "-" : p.ActionBy}`
          : p.Status === "Rejected"
            ? `Rejected By - ${p.ActionBy === "NULL" ? "-" : p.ActionBy}`
            : p.ActionBy === "NULL"
              ? "-"
              : p.ActionBy;

      const showHeadId = UserRole !== "State" || RoleID === 29;
      const showAction = RoleID === 14 || RoleID === 31 || RoleID === 80;

      const excelData = bills.map((proposal, index) => {
        const row = {
          "Sr.No.": index + 1,
          "Demand ID": proposal.DemandGenerationID,
          "RA bill number": proposal.RABillNumber,
          "Class Of Work": proposal.classOfWork || proposal.workClass || "-",
          "Type Of Work": proposal.typeOfWork || proposal.workType || "-",
          "Work Order Type": proposal.WorkOrderType || proposal.workOrderType || "-",
          "Work Name": proposal.ProjectID,
          District: proposal.District,
          Taluka: proposal.ulb || proposal.Taluka || "-",
          "Work Apprved Year": proposal.WorkApprovedYear || "-",
          "Work Apprved GR":
            proposal.GRApproveDate || proposal.workApprovedGRDate
              ? new Date(proposal.GRApproveDate || proposal.workApprovedGRDate).toLocaleDateString("en-GB")
              : "-",
          "Technical Sanction Number": proposal.BillNumber || "-",
        };

        if (showHeadId) row["Head ID"] = proposal.BudgetID || "-";

        Object.assign(row, {
          "Job ID": proposal.JOB_ID || "-",
          "Physical Progress(in %)": proposal.physicalValueOfProgress || "-",
          "Vendor Name": proposal.ContractorName || "-",
          "Bill Copy": proposal.UploadBillCopy || "No File",
          "Measurement Book": proposal.Measurement_Book || "No File",
          "SQM Inspection Book": proposal.Inspection_Report || "No File",
          "Work completion Certificate Book": proposal.Work_completion || "No File",
          "Administrative Approved GR Copy": proposal.GRCopy || "No File",
          Remark: proposal.Remark || "-",
          "Reject/SentBack Remark": proposal.Remark || "-",
          "Bill Amount": proposal.BillAmount || 0,
          "Net Payable Amount": proposal.NetPayableAmount || 0,
          "Deduction Amount": proposal.DeductionAmount || 0,
          "Creation Date": proposal.Date ? new Date(proposal.Date).toLocaleString("en-IN") : "-",
          "Upload Demand Copy": proposal.UploadBillCopy || "No File",
          "Demand Copy": proposal.BillCopy || "No File",
          "Action By": actionBy(proposal),
        });

        // approval columns follow the district/department flow
        outwardColumns.forEach((col) => {
          row[col.label] = isStageApplicable(proposal, col.stage) ? getStatus(proposal[col.stage]) : "-";
        });
        row["RDD"] = getStatus(proposal.Approvance9);

        if (showAction) row["Action"] = proposal.Status || "-";
        return row;
      });

      const totalRow = {
        "Sr.No.": "Total",
        "Demand ID": "",
        "RA bill number": "",
        "Class Of Work": "",
        "Type Of Work": "",
        "Work Order Type": "",
        "Work Name": "",
        District: "",
        Taluka: "",
        "Work Apprved Year": "",
        "Work Apprved GR": "",
        "Technical Sanction Number": "",
      };
      if (showHeadId) totalRow["Head ID"] = "";

      Object.assign(totalRow, {
        "Job ID": "",
        "Physical Progress(in %)": "",
        "Vendor Name": "",
        "Bill Copy": "",
        "Measurement Book": "",
        "SQM Inspection Book": "",
        "Work completion Certificate Book": "",
        "Administrative Approved GR Copy": "",
        Remark: "",
        "Reject/SentBack Remark": "",
        "Bill Amount": sumOf(bills, "BillAmount"),
        "Net Payable Amount": sumOf(bills, "NetPayableAmount"),
        "Deduction Amount": sumOf(bills, "DeductionAmount"),
        "Creation Date": "",
        "Upload Demand Copy": "",
        "Demand Copy": "",
        "Action By": "",
      });
      outwardColumns.forEach((col) => {
        totalRow[col.label] = "";
      });
      totalRow["RDD"] = "";
      if (showAction) totalRow["Action"] = "";

      excelData.push(totalRow);

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet["!cols"] = Object.keys(excelData[0]).map(() => ({ wch: 22 }));
      worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };
      worksheet["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: excelData.length, c: Object.keys(excelData[0]).length - 1 },
        }),
      };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Bills");
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error("Excel Export Error:", error);
    } finally {
      setloading(false);
    }
  };

  /* ---------- PDF export (table) ---------- */
  const downloadTableOnlyAsPDF = async (tableClass, filename, title, chunkSize = 500) => {
    const originalTable = document.querySelector(`.${tableClass}`);
    if (!originalTable) {
      setExportingPDF(false);
      return;
    }

    setloading(true);
    setExportingPDF(true);
    await new Promise((resolve) => setTimeout(resolve, 600));

    try {
      const totalRows = originalTable.querySelectorAll("tbody tr").length;
      if (!totalRows) {
        setloading(false);
        setExportingPDF(false);
        return;
      }

      for (let start = 0; start < totalRows; start += chunkSize) {
        const end = Math.min(start + chunkSize, totalRows);

        const tableClone = originalTable.cloneNode(true);
        tableClone.querySelectorAll("tbody tr").forEach((row, index) => {
          if (index < start || index >= end) row.remove();
        });

        tableClone.style.position = "absolute";
        tableClone.style.left = "-9999px";
        tableClone.style.top = "0";
        document.body.appendChild(tableClone);

        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const topMargin = 25;
        const sideMargin = 10;
        const bottomMargin = 15;

        pdf.setFontSize(16);
        pdf.setFont("helvetica", "bold");
        pdf.text(`${title} (${start + 1} - ${end})`, pageWidth / 2, 15, { align: "center" });

        const canvas = await html2canvas(tableClone, { scale: 1, useCORS: true, backgroundColor: "#2C78DB" });

        const imgWidth = pageWidth - sideMargin * 2;
        const pxToMm = imgWidth / canvas.width;
        const usablePageHeightPx = (pageHeight - topMargin - bottomMargin) / pxToMm;

        let renderedHeight = 0;
        let pageNumber = 0;

        while (renderedHeight < canvas.height) {
          const sliceHeight = Math.min(usablePageHeightPx, canvas.height - renderedHeight);
          const pageCanvas = document.createElement("canvas");
          const ctx = pageCanvas.getContext("2d");
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceHeight;
          ctx.drawImage(canvas, 0, renderedHeight, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

          if (pageNumber > 0) pdf.addPage();
          pdf.addImage(
            pageCanvas.toDataURL("image/jpeg", 0.85),
            "JPEG",
            sideMargin,
            topMargin,
            imgWidth,
            sliceHeight * pxToMm,
          );
          renderedHeight += sliceHeight;
          pageNumber++;
        }

        pdf.save(`${filename}_${start + 1}_to_${end}.pdf`);
        document.body.removeChild(tableClone);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error) {
      console.error("PDF Export Error:", error);
    } finally {
      setExportingPDF(false);
      setloading(false);
    }
  };

  /* ---------- bill slip ---------- */
  const settingshowspecific3 = async (ID) => {
    try {
      const res = await Getyears("FetchDemandDeductionDetailbyBillID", ID);
      setfetchSpecficDemandDeductionDetail(res);
      setSelectedDemandID(ID);
      setShowSpecific(4);
      setselectedBillData(res.filter((val) => val.DemandGenerationID == ID)[0]);
    } catch (err) {
      console.log(err);
    }
  };

  const exportBillSlipPDF = async () => {
    if (!billSlipRef.current) {
      alert("Bill slip not found");
      return;
    }
    setExportingBillSlipPDF(true);
    try {
      const canvas = await html2canvas(billSlipRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const ratio = Math.min((pageWidth - margin * 2) / canvas.width, (pageHeight - margin * 2) / canvas.height);
      const imgWidth = canvas.width * ratio;
      const imgHeight = canvas.height * ratio;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", (pageWidth - imgWidth) / 2, margin, imgWidth, imgHeight);
      const transactionNo = selectedBillData?.DemandGenerationID || selectedDemandID;
      pdf.save(`Bill_Slip_${transactionNo || "RDD"}.pdf`);
    } catch (error) {
      console.error("Bill slip PDF export error:", error);
      alert("Failed to export bill slip PDF. Please try again.");
    } finally {
      setExportingBillSlipPDF(false);
    }
  };

  const BackFunction = () => {
    setfetchSpecficDemandDeductionDetail([]);
    setselectedBillData([]);
    setShowSpecific(0);
  };

  const DemandDeductionDetailByBillID = async (ID) => {
    try {
      const res = await Getyears("FetchDemandDeductionDetailbyBillID", ID);
      setfetchSpecficDemandDeductionDetail(res);
      setSelectedDemandID(ID);
      setShowSpecific(5);
    } catch (err) {
      console.log(err);
    }
  };

  const openProgressModal = (images, location) => {
    try {
      const parsedImages = JSON.parse(images || "[]");
      const parsedLocations = JSON.parse(location || "[na]");

      const imageArray = [];
      parsedImages.forEach((img) => {
        try {
          const nested = JSON.parse(img);
          if (Array.isArray(nested)) imageArray.push(...nested);
          else imageArray.push(img);
        } catch {
          imageArray.push(img);
        }
      });

      const combinedData = imageArray.map((image, index) => {
        let locationObj = {};
        const loc = parsedLocations[index];
        if (loc && loc !== "na" && loc !== "null" && loc !== "") {
          try {
            locationObj = JSON.parse(loc);
          } catch (err) {
            console.error("Invalid location JSON:", loc, err);
          }
        }
        return { image, location: locationObj };
      });

      setProgressData(combinedData);
      setShowProgressModal(true);
    } catch (err) {
      console.log(err);
    }
  };

  /* ---------- small render helpers ---------- */
  const hth = (label, width) => (
    <th key={label} style={{ minWidth: `${width}px`, padding: "8px" }} rowSpan={2} colSpan={1}>
      {t(label)}
    </th>
  );

  const fileCell = (file, folder) =>
    file ? (
      <a href={`${apiurl}/${folder}/${file}`} target="_blank" rel="noreferrer">
        {formatFileName(file)}
      </a>
    ) : (
      <p className="fw-bold text-danger my-auto">{t("No File")}</p>
    );

  const showHeadIdCol = !(UserRole === "State" && RoleID != 29);
  const showActionCol = RoleID === 14 || RoleID === 31 || RoleID === 80;

  const slipHeaderCell = {
    padding: "6px 8px", fontWeight: 500, backgroundColor: "#1190CA", color: "white",
  };
  const slipValueCell = { padding: "6px 8px", backgroundColor: "white" };

  /* ================================================================== */
  /*  RENDER                                                            */
  /* ================================================================== */
  return (
    <>
      <div
        className="col-12"
        style={{
          backgroundColor: "#EFF3F8",
          height: "89vh",
          overflowX: "hidden",
          overflowY: "scroll",
          position: "relative",
        }}
      >
        {showspecific === 0 && (
          <div
            style={{
              filter: loading ? "blur(4px)" : "none",
              pointerEvents: loading ? "none" : "auto",
              transition: "0.2s ease",
            }}
          >
            <div className="col-11 m-auto">
              <div className="col-12 ">
                <div className="col-12 px-4 mb-4 mt-3">
                  {/* ================= ROW 1 : HEADING + DOWNLOAD ================= */}
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                    <h5 className="fw-bold d-flex align-items-center m-0">
                      <img
                        src="/GR-Approval64.png"
                        alt="Technical Approval"
                        style={{ width: "24px", height: "24px", marginRight: "6px" }}
                      />
                      {[24, 25, 29, 40, 41].includes(RoleID) ? t("Bill Status") : t("Bill Outward")}
                    </h5>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success"
                        onClick={() => exportTableByClassToExcel(filteredBills, "Bills_Outward.xlsx")}
                      >
                        {t("Download Excel")}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() =>
                          downloadTableOnlyAsPDF("BillsOutwardTable", "Bills_Outward.pdf", "Bills Outward Report")
                        }
                      >
                        {t("Download PDF")}
                      </button>
                    </div>
                  </div>

                  {/* ================= ROW 2 : FILTERS + SEARCH ================= */}
                  <div
                    style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", width: "100%" }}
                  >
                    {/* Type of Data */}
                    {UserRole === "State" && RoleID == 29 && (
                      <div style={{ width: "150px", flexShrink: 0 }}>
                        <select
                          value={typeofData}
                          onChange={(e) => settypeofData(e.target.value)}
                          style={selectBoxStyle}
                        >
                          <option value="" disabled>
                            Choose Data
                          </option>
                          <option value="District">
                            {t("3054-2419(Roads and bridge repair maintenance program)")}
                          </option>
                          <option value="State">{t("2515-1238(MLA Representative)")}</option>
                        </select>
                      </div>
                    )}

                    {/* Department (only when the login has more than one) */}
                    {departments.length > 1 && (
                      <div style={{ width: "190px", flexShrink: 0 }}>
                        <select
                          value={selectedDepartmentId}
                          onChange={handleDepartmentChange}
                          style={selectBoxStyle}
                        >
                          {departments.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Status */}
                    <div style={{ width: "150px", flexShrink: 0 }}>
                      <select
                        value={statusofData1}
                        onChange={(e) => setstatusofData1(e.target.value)}
                        style={selectBoxStyle}
                      >
                        <option value="">{t("All")}</option>
                        <option value="Approved">{t("Approved")}</option>
                        <option value="false">{t("Pending")}</option>
                        <option value="Rejected">{t("Rejected")}</option>
                        <option value="SendBack">{t("Sent Back")}</option>
                      </select>
                    </div>

                    {/* District */}
                    {UserRole === "State" && (
                      <MultiSelectBox
                        placeholder="Select District"
                        allLabel={t("All Districts")}
                        options={allDistricts}
                        selected={SelectedDistrict}
                        onToggle={handleDistrictToggle}
                        onToggleAll={toggleAllDistricts}
                        allSelected={isAllSelected}
                        open={openDistrict}
                        setOpen={setOpenDistrict}
                      />
                    )}

                    {/* Head Code */}
                    {![24, 25, 40, 41].includes(RoleID) && (
                      <div style={{ width: "195px", flexShrink: 0 }}>
                        <select
                          value={selectedHeadCode}
                          onChange={(e) => setSelectedHeadCode(e.target.value)}
                          style={selectBoxStyle}
                        >
                          <option value="">{t("Select Head Code")}</option>
                          {[...new Set(deptScopedBills.map((item) => item.BudgetID).filter(Boolean))]
                            .sort()
                            .map((headCode, index) => (
                              <option key={index} value={headCode}>
                                {headCode}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}

                    {/* Taluka */}
                    <MultiSelectBox
                      placeholder="Select Taluka"
                      allLabel={t("All Talukas")}
                      options={allTalukas}
                      selected={SelectedTaluka}
                      onToggle={handleTalukaToggle}
                      onToggleAll={toggleAllTalukas}
                      allSelected={isAllTalukaSelected}
                      open={openTaluka}
                      setOpen={setOpenTaluka}
                    />

                    {/* From + To */}
                    <div style={{ display: "flex", gap: "10px", alignItems: "center", flexShrink: 0 }}>
                      <div className="input-group" style={{ width: "150px", height: "40px" }}>
                        <span className="input-group-text">From</span>
                        <input
                          type="date"
                          className="form-control"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          style={{ fontSize: "14px", height: "40px" }}
                        />
                      </div>
                      <div className="input-group" style={{ width: "150px", height: "40px" }}>
                        <span className="input-group-text">To</span>
                        <input
                          type="date"
                          className="form-control"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          style={{ fontSize: "14px", height: "40px" }}
                        />
                      </div>
                    </div>

                    {/* Year */}
                    <div style={{ width: "150px", flexShrink: 0 }}>
                      <select
                        value={approvedYear}
                        onChange={(e) => setApprovedYear(e.target.value)}
                        style={selectBoxStyle}
                      >
                        <option value="">{t("Select Year")}</option>
                        {[...new Set(deptScopedBills.map((item) => item.WorkApprovedYear).filter(Boolean))]
                          .sort()
                          .map((year, index) => (
                            <option key={index} value={year}>
                              {year}
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Work Order Type */}
                    <div style={{ width: "195px", flexShrink: 0 }}>
                      <select
                        value={selectedWorkOrderType}
                        onChange={(e) => setSelectedWorkOrderType(e.target.value)}
                        style={selectBoxStyle}
                      >
                        <option value="">Select Work Order Type</option>
                        {[
                          ...new Set(
                            deptScopedBills
                              .map((item) => item.WorkOrderType || item.workOrderType || item.Work_Order_Type)
                              .filter(Boolean),
                          ),
                        ]
                          .sort()
                          .map((type, index) => (
                            <option key={index} value={type}>
                              {type}
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Search */}
                    <div style={{ position: "relative", width: "220px", flexShrink: 0 }}>
                      <img
                        src="/Search32.png"
                        alt="Search"
                        style={{
                          position: "absolute", top: "50%", left: "10px",
                          transform: "translateY(-50%)", width: "16px", opacity: 0.6,
                        }}
                      />
                      <input
                        type="text"
                        placeholder={t("Search Here")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: "100%", height: "40px", boxSizing: "border-box",
                          padding: "8px 12px 8px 32px", border: "1px solid #ccc",
                          borderRadius: "8px", backgroundColor: "#f1f6fc", fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* ================= TABLE ================= */}
                <div className="col-12 pb-5">
                  <div
                    className="m-auto mb-3"
                    style={{
                      backgroundColor: "white",
                      padding: "40px 40px",
                      boxShadow: "0px 0px 5px 1px #E4E8EC",
                      borderRadius: "20px",
                      width: "100%",
                      maxWidth: "100%",
                      overflowX: "hidden",
                    }}
                  >
                    <div
                      className="col-12 tableDesign"
                      style={{
                        background: "#2C78DB",
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: "600px",
                        position: "relative",
                      }}
                    >
                      <table className="m-0 p-0 BillsOutwardTable" style={{ background: "#2C78DB" }}>
                        <thead style={{ position: "sticky", backgroundColor: "#1976d2", top: 0, zIndex: 10 }}>
                          <tr style={{ color: "white", border: "none", height: "3rem", fontSize: "18px" }}>
                            {hth("Sr.No.", 70)}
                            {hth("Demand ID", 130)}
                            {hth("RA Bill Number", 130)}
                            {hth("Class Of Work", 130)}
                            {hth("Type Of Work", 130)}
                            {hth("Work Order Type", 160)}
                            {hth("Work Name", 250)}
                            {hth("District", 150)}
                            {hth("Taluka", 150)}
                            {hth("Work Apprved Year", 150)}
                            {hth("Administrative Approved GR Date", 150)}
                            {hth("Technical Sanction Number", 150)}
                            {showHeadIdCol && hth("Head ID", 250)}
                            {hth("Job ID", 250)}
                            {hth("Physical Progress(in %)", 150)}
                            {hth("Vendor Name", 150)}
                            {hth("Bill Copy", 250)}
                            {hth("Measurement Book", 250)}
                            {hth("SQM Inspection Book", 250)}
                            {hth("Work Completion Certificate", 250)}
                            {hth("Administrative Approved GR Copy", 250)}
                            {hth("Remark", 150)}
                            {hth("Reject/SentBack Remark", 150)}
                            {hth("Bill Amount", 150)}
                            {hth("Net Payable Amount", 150)}
                            {hth("Deduction Amount", 150)}
                            {hth("Creation Date", 190)}
                            {showActionCol && hth("Action", 120)}
                            {hth("Action By", 120)}
                            <th style={{ padding: "8px" }} colSpan={outwardColumns.length + 1}>
                              {t("Approved by")}
                            </th>
                          </tr>

                          <tr style={{ color: "white", border: "none", height: "3rem", fontSize: "18px" }}>
                            {outwardColumns.map((col) => (
                              <th key={col.stage} style={{ minWidth: "150px", padding: "8px" }}>
                                {col.stage === "Approvance1" && isChecker ? t("Checker") : t(col.label)}
                              </th>
                            ))}
                            <th style={{ minWidth: "150px", padding: "8px" }}>{t("RDD")}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {displayedGRS?.length === 0 ? (
                            <tr>
                              <td
                                colSpan="30"
                                style={{ textAlign: "center", padding: "20px", backgroundColor: "#f9f9f9" }}
                              >
                                {t("No data available")}
                              </td>
                            </tr>
                          ) : (
                            displayedGRS?.map((proposal, index) => {
                              const bgColor = index % 2 === 0 ? "#E5F4FB" : "#CFE4F1";
                              return (
                                <tr
                                  style={{ backgroundColor: bgColor, fontSize: "18px" }}
                                  key={proposal.DemandGenerationID || index}
                                >
                                  <td style={{ padding: "8px 8px" }}>{indexOfFirstBill + index + 1}</td>
                                  <td style={{ padding: "8px 8px" }}>
                                    <span
                                      onClick={() => settingshowspecific3(proposal.DemandGenerationID)}
                                      style={{
                                        color: "#0d6efd", textDecoration: "underline",
                                        cursor: "pointer", fontWeight: "500",
                                      }}
                                    >
                                      {proposal.DemandGenerationID}
                                    </span>
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>{proposal.RABillNumber}</td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {proposal.classOfWork || proposal.workClass}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {proposal.typeOfWork && proposal.typeOfWork !== "NULL"
                                      ? proposal.typeOfWork
                                      : proposal.workType && proposal.workType !== "NULL"
                                        ? proposal.workType
                                        : "-"}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {proposal.WorkOrderType || proposal.workOrderType || "-"}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>{proposal.ProjectID}</td>
                                  <td style={{ padding: "8px 8px" }}>{proposal.District}</td>
                                  <td style={{ padding: "8px 8px" }}>{proposal.ulb || proposal.Taluka || "-"}</td>
                                  <td
                                    style={{ width: "140px", minWidth: "140px", padding: "10px", textAlign: "center" }}
                                  >
                                    {proposal.WorkApprovedYear || "-"}
                                  </td>
                                  <td>
                                    {proposal.GRApproveDate || proposal.workApprovedGRDate
                                      ? formatDateTime(proposal.GRApproveDate || proposal.workApprovedGRDate)
                                      : "-"}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>{proposal.BillNumber || "-"}</td>
                                  {showHeadIdCol && <td style={{ padding: "8px 8px" }}>{proposal.BudgetID}</td>}
                                  <td style={{ padding: "8px 8px" }}>{proposal.JOB_ID || "-"}</td>
                                  <td>
                                    <span
                                      style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                                      onClick={() => openProgressModal(proposal.Upload, proposal.Location)}
                                    >
                                      {proposal.physicalValueOfProgress}%
                                    </span>
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>{proposal.ContractorName}</td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {fileCell(proposal.UploadBillCopy, "BillCopyDocsImages")}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {fileCell(proposal.Measurement_Book, "Measurement_BookImages")}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {fileCell(proposal.Inspection_Report, "Inspection_ReportImages")}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {fileCell(proposal.Work_completion, "Work_completionImages")}
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>{fileCell(proposal.GRCopy, "UploadedGR")}</td>
                                  <td style={{ padding: "8px 8px" }}>{proposal.Remark}</td>
                                  <td style={{ padding: "8px 8px" }}>
                                    {proposal.ActionDemandRemark === "NULL"
                                      ? "-"
                                      : proposal.ActionDemandRemark || "-"}
                                  </td>
                                  {/* <td style={{ padding: "8px 8px" }}>{proposal.BillAmount}</td> */}
                                  <td style={{ padding: "8px 8px" }}>{Number(proposal.BillAmount).toLocaleString("en-IN")}</td>
                                  {/* <td style={{ padding: "8px 8px" }}>{proposal.NetPayableAmount}</td> */}
                                  <td style={{ padding: "8px 8px" }}>{Number(proposal.NetPayableAmount).toLocaleString("en-IN")}</td>
                                  <td onClick={() => DemandDeductionDetailByBillID(proposal.DemandGenerationID)}>
                                    <p className="makeitlink my-auto" style={{ textAlign: "center" }}>
                                      {Number(proposal.DeductionAmount).toLocaleString("en-IN")}
                                    </p>
                                  </td>
                                  <td style={{ padding: "8px 8px" }}>{formatDateTime(proposal.Date)}</td>
                                  {showActionCol && (
                                    <td style={{ padding: "8px 8px" }}>
                                      {proposal.Status === "SendBack" && proposal.UserID == currentUserID ? (
                                        <button
                                          onClick={() =>
                                            navigate(
                                              `/AdminDashboard/EditDemandGeneration/${proposal.DemandGenerationID}`,
                                            )
                                          }
                                          style={{
                                            background: "linear-gradient(to right, #f0ad4e, #ec971f)",
                                            color: "white", border: "none", padding: "6px 14px",
                                            borderRadius: "16px", fontSize: "12px",
                                            fontWeight: "600", cursor: "pointer",
                                          }}
                                        >
                                          {t("Edit")}
                                        </button>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                  )}
                                  <td style={{ padding: "8px 8px" }}>
                                    {proposal.Status === "SendBack"
                                      ? `Send Back By - ${proposal.ActionBy === "NULL" ? "-" : proposal.ActionBy}`
                                      : proposal.Status === "Rejected"
                                        ? `Rejected By - ${proposal.ActionBy === "NULL" ? "-" : proposal.ActionBy}`
                                        : proposal.ActionBy === "NULL"
                                          ? "-"
                                          : proposal.ActionBy || "-"}
                                  </td>

                                  {/* approval cells follow the district/department flow */}
                                  {outwardColumns.map((col) => (
                                    <td key={col.stage} style={{ padding: "8px 8px" }}>
                                      {isStageApplicable(proposal, col.stage)
                                        ? renderTick(proposal[col.stage])
                                        : "-"}
                                    </td>
                                  ))}
                                  <td style={{ padding: "8px 8px" }}>{renderTick(proposal.Approvance9)}</td>
                                </tr>
                              );
                            })
                          )}

                          <tr style={{ fontWeight: "600", backgroundColor: "#f0f0f0", fontSize: "18px" }}>
                            <td
                              colSpan={UserRole == "State" && RoleID != 34 ? 22 : 23}
                              style={{ textAlign: "right", padding: "8px" }}
                            >
                              {t("Page Total")}
                            </td>
                            <td style={{ padding: "8px" }}>₹ {pagetotalBillamt.toLocaleString("en-IN")}</td>
                            <td style={{ padding: "8px" }}>₹ {pagetotalpayableamt.toLocaleString("en-IN")}</td>
                            <td style={{ padding: "8px" }}>₹ {pagetotaldeductionamt.toLocaleString("en-IN")}</td>
                            <td colSpan={UserRole == "State" ? 22 : 23}></td>
                          </tr>
                          <tr style={{ fontWeight: "600", backgroundColor: "#f0f0f0", fontSize: "18px" }}>
                            <td
                              colSpan={UserRole == "State" && RoleID != 34 ? 22 : 23}
                              style={{ textAlign: "right", padding: "8px" }}
                            >
                              {t("Gross Total")}
                            </td>
                            <td style={{ padding: "8px" }}>₹ {totalBillamt.toLocaleString("en-IN")}</td>
                            <td style={{ padding: "8px" }}>₹ {totalpayableamt.toLocaleString("en-IN")}</td>
                            <td style={{ padding: "8px" }}>₹ {totaldeductionamt.toLocaleString("en-IN")}</td>
                            <td colSpan={UserRole == "State" ? 22 : 23}></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* ================= FOOTER / PAGINATION ================= */}
                    <div
                      style={{ display: "flex", justifyContent: "space-between", height: "4rem", alignItems: "center" }}
                      className="mt-3"
                    >
                      <div>
                        <p className="text-gray-600" style={{ fontSize: "18px" }}>
                          {proposallist.length > 0
                            ? `Showing ${filteredBills.length === 0 ? 0 : indexOfFirstBill + 1} to ${Math.min(
                                indexOfLastBill,
                                filteredBills.length,
                              )} of ${totalBillsCount} entries`
                            : "Showing 0 to 0 of 0 entries"}
                        </p>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <GooglePagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                        <button
                          onClick={() => {
                            setShowAll(!showAll);
                            setCurrentPage(1);
                          }}
                          style={{
                            border: "none", outline: "none", cursor: "pointer",
                            padding: "8px 16px", borderRadius: "20px", fontSize: "18px",
                            fontWeight: "600", color: "#fff",
                            background: showAll
                              ? "linear-gradient(135deg, #28a745, #20c997)"
                              : "linear-gradient(135deg, #0067ab, #0098d5)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {showAll ? "✓ Showing All" : "View All"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= BILL SLIP ================= */}
        {showspecific === 4 && (
          <div className="col-12 m-auto">
            <div
              className="col-12 m-auto my-15 mb-10 budgetContainer "
              style={{
                maxHeight: "85vh", overflowY: "auto", overflowX: "hidden",
                border: "1px solid #ddd", borderRadius: "8px",
              }}
            >
              <div className="m-auto budgetElement">
                <div
                  className="mt-3 d-flex justify-content-between"
                  style={{ position: "relative", alignItems: "center", background: "#fff" }}
                >
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <img src="/Bill-files64.png" alt="MAS Slip" width="26" height="26" />
                    <h5 className="m-0 fw-bold">Bill Slip</h5>
                  </div>

                  <div className="col-4 pb-3 d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-primary d-flex align-items-center gap-2"
                      style={{ borderRadius: "999px", padding: "6px 22px", fontSize: "14px", zIndex: 10 }}
                      onClick={exportBillSlipPDF}
                      disabled={exportingBillSlipPDF}
                    >
                      {exportingBillSlipPDF ? "Exporting..." : "Export PDF"}
                    </button>
                    <button
                      className="btn btn-danger d-flex align-items-center gap-2"
                      style={{ borderRadius: "999px", padding: "6px 22px", fontSize: "14px", zIndex: 10 }}
                      onClick={BackFunction}
                    >
                      Back
                    </button>
                  </div>
                </div>

                <div ref={billSlipRef}>
                  <div
                    className="text-center text-white py-3"
                    style={{ background: "linear-gradient(90deg, #2b4ea1, #0aa2d6)" }}
                  >
                    <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                      Zila Parishad - {selectedBillData.District}
                    </div>
                  </div>

                  <div className="border border-dark p-1">
                    <table className="table mb-0" style={{ fontSize: "18px", border: "none" }}>
                      <tbody>
                        <tr>
                          <td colSpan={2} className="fw-bold text-center fs-6 p-1">
                            {" "}
                            Bill Slip for RDD
                          </td>
                        </tr>
                        {[
                          ["User Name", `${selectedBillData.UploadedBy ?? "-"}`],
                          ["Demand ID", `${selectedBillData.DemandGenerationID ?? "-"}`],
                          ["Bill Creation Date", formatDateTime(selectedBillData.UploadedOn)],
                          ["Head Code", `${selectedBillData.BudgetID ?? "-"}`],
                          ["Work Name", `${selectedBillData.ProjectID ?? "-"}`],
                          [
                            "Class of Work",
                            `${selectedBillData.classOfWork || selectedBillData.workClass || "-"}`,
                          ],
                          ["Work Approved Year", `${selectedBillData.ProposalYear ?? "-"}`],
                          ["Work order date", formatDate(selectedBillData.WorkOrderDate)],
                          ["Work Approved Amount", `${selectedBillData.ApprovedWorkAmount ?? "-"}`],
                          ["Gross Bill Amount", `${selectedBillData.BillAmount ?? "-"}`],
                          ["R.A.Bill No:", `${selectedBillData.RABillNumber ?? "-"}`],
                          ["Invoice Bill No", `${selectedBillData.InvoiceNo ?? "-"}`],
                        ].map(([label, value], index) => (
                          <tr key={index} style={{ border: "1.5px solid #1190CA" }}>
                            <td
                              className="text-start"
                              style={{
                                width: "30%", backgroundColor: "#EAF3FB", fontWeight: 500,
                                padding: "6px 10px", borderRight: "1.5px solid #1190CA",
                              }}
                            >
                              {label}
                            </td>
                            <td
                              className="text-start"
                              style={{ width: "70%", padding: "6px 10px", backgroundColor: "#FFFFFF" }}
                            >
                              {value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <table className="mt-1" style={{ borderCollapse: "collapse", border: "1px solid #000" }}>
                      <tbody>
                        <tr className="border border-dark">
                          <td className="text-start border border-dark " style={{ width: "30%" }}>
                            Bill Amount
                          </td>
                          <td className="text-start border border-dark" style={{ width: "23%" }}>
                            Deduction Amount
                          </td>
                          <td className="text-start border border-dark " style={{ width: "24%" }}>
                            Net Payable
                          </td>
                        </tr>
                        <tr>
                          <td className="text-start border border-dark">{selectedBillData.BillAmount}</td>
                          <td className="text-start border border-dark">{selectedBillData.DeductionAmount}</td>
                          <td className="text-start border border-dark">{selectedBillData.NetPayableAmount}</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Vendor Details */}
                    <table className="mt-2" style={{ width: "100%", borderCollapse: "collapse", fontSize: "18px" }}>
                      <tbody>
                        <tr style={{ border: "1.5px solid #1190CA", backgroundColor: "#EAF3FB" }}>
                          <td colSpan="6" style={{ padding: "8px 10px", fontWeight: 600, textAlign: "center" }}>
                            Vendor Details
                          </td>
                        </tr>
                        <tr style={{ border: "1.5px solid #1190CA" }}>
                          <td style={slipHeaderCell}>Vendor Name</td>
                          <td style={slipHeaderCell}>IFSC Code</td>
                          <td style={slipHeaderCell}>Branch Name</td>
                          <td style={slipHeaderCell}>Bank Name</td>
                          <td style={slipHeaderCell}>Account Number</td>
                          <td style={slipHeaderCell}>Amount </td>
                        </tr>

                        {fetchSpecificDemandDeductionDetail &&
                          fetchSpecificDemandDeductionDetail
                            .filter((val) => val.TypeOfAmount == "Total Amount Payable")
                            .map((val, index) => (
                              <tr style={{ border: "1.5px solid #1190CA" }} key={index}>
                                <td style={slipValueCell}>{val.Vendor_Company_Name}</td>
                                <td style={slipValueCell}>{val.Bank_IFSC_Code}</td>
                                <td style={slipValueCell}>{val.Branch_Name}</td>
                                <td style={slipValueCell}>{val.BANK_NAME}</td>
                                <td style={slipValueCell}>{val.Bank_Account_Number}</td>
                                <td style={slipValueCell}>{val.TotalAmount}</td>
                              </tr>
                            ))}

                        <tr style={{ border: "1.5px solid #1190CA" }}>
                          <td
                            style={{ padding: "6px 8px", backgroundColor: "#EAF3FB", fontWeight: 500 }}
                            colSpan={5}
                          >
                            Total
                          </td>
                          <td className="border border-dark">{selectedBillData.NetPayableAmount}</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Deductions */}
                    <table className="my-2" style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ border: "1.5px solid #1190CA", backgroundColor: "#EAF3FB" }}>
                          <td colSpan="6" style={{ padding: "8px 10px", fontWeight: 600, textAlign: "center" }}>
                            Deduction Amount Details
                          </td>
                        </tr>
                        <tr style={{ color: "white", border: "none", height: "3rem" }}>
                          <th style={{ minWidth: "60px", padding: "8px" }}>Sr No</th>
                          <th style={{ minWidth: "60px", padding: "8px" }}>Deduction Type</th>
                          <th style={{ minWidth: "60px", padding: "8px" }}>Deduction Amount </th>
                          <th style={{ minWidth: "60px", padding: "8px" }}>Account Number</th>
                          <th style={{ minWidth: "60px", padding: "8px" }}>Bank Name</th>
                          <th style={{ minWidth: "60px", padding: "8px" }}>IFSC</th>
                        </tr>
                      </thead>

                      <tbody>
                        {fetchSpecificDemandDeductionDetail.filter(
                          (val) => val.TypeOfAmount != "Total Amount Payable",
                        )?.length == 0 ? (
                          <tr>
                            <td
                              colSpan="30"
                              style={{ textAlign: "center", padding: "20px", backgroundColor: "#f9f9f9" }}
                            >
                              No data available
                            </td>
                          </tr>
                        ) : (
                          fetchSpecificDemandDeductionDetail
                            .filter((val) => val.TypeOfAmount != "Total Amount Payable")
                            .map((row, index) => (
                              <tr
                                className="px-3"
                                key={index}
                                style={{
                                  borderBottom: "1px solid #3b409e",
                                  backgroundColor: index % 2 === 0 ? "#E5F4FB" : "#CFE4F1",
                                  fontSize: "18px",
                                }}
                              >
                                <td style={{ fontSize: "13px", padding: "5px" }}>{index + 1}</td>
                                <td style={{ fontSize: "13px", padding: "5px" }}>{row.TypeOfAmount}</td>
                                <td style={{ fontSize: "13px", padding: "5px" }}>{row.TotalAmount}</td>
                                <td style={{ fontSize: "13px", padding: "5px" }}>{row.Bank_Account_Number}</td>
                                <td style={{ fontSize: "13px", padding: "5px" }}>{row.Bank_Name}</td>
                                <td style={{ fontSize: "13px", padding: "5px" }}>{row.Bank_IFSC_Code}</td>
                              </tr>
                            ))
                        )}

                        <tr style={{ border: "1.5px solid #1190CA", fontWeight: 600, background: "white" }}>
                          <td colSpan="2" style={{ padding: "6px 8px", textAlign: "right" }}>
                            Total
                          </td>
                          <td style={{ padding: "6px 8px" }}>{selectedBillData.DeductionAmount}</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Footer text */}
                    <table style={footerTitleStyle}>
                      <tbody>
                        <tr>
                          <td style={footerTitleTdStyle}>मागील देयक तपशील</td>
                        </tr>
                      </tbody>
                    </table>

                    <table style={remarksTableStyle}>
                      <tbody>
                        <tr>
                          <td style={remarksTableTdStyle}>
                            प्रमाणित करण्यात येते की मा देयकाद्वारे आहारीत करण्यात येत असलेली संपूर्ण रक्कम याच
                            जिल्हात खर्च करण्यात येणार आहे.
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <table style={remarksTableStyle}>
                      <tbody>
                        <tr>
                          <td style={remarksTableTdStyle}>स्वाक्षरी आहरण व संवितरण अधिकारी</td>
                        </tr>
                      </tbody>
                    </table>

                    <table style={instructionTableStyle}>
                      <tbody>
                        <tr>
                          <td style={instructionTableTdStyle}>
                            दुबार देयक प्रतिबंध नोंदवही तपासणीच्या आधारे प्रमाणित करण्यात येते की या देयकात नमूद
                            केलेल्या अधिकाऱ्याचे कर्मचाऱ्याचे / पुरवठादाराचे तसेच त्यात नमूद केलेल्या बाबीचे /
                            कालावधीचे देयक यापूर्वी ग्रामविकास विभाग मुबई येथे सादर करण्यात आले नव्हते. प्रमाणित
                            करण्यात येते की वरील मागणी ही महाराष्ट्र कोषागार नियम / मुंबई वित्तीय नियम /
                            महाराष्ट्र आकस्मिक खर्च नियम व वेळोवेळी विहित केलेल्या आदेशांच्या आधारे तयार
                            करण्यात आली आहे. श्री. किंवा श्रीमती ______________________________ यांना सदर
                            देयकाची रक्कम/किंवा अपेक्षित देयक प्रदान करीता प्राधिकृत करण्यात आले असून त्यांची
                            स्वाक्षरी खालीलप्रमाणे आहे.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= DEDUCTION DETAIL ================= */}
        {selectedDemandID && showspecific === 5 && (
          <div
            className="col-12"
            style={{ backgroundColor: "#EFF3F8", height: "90vh", overflowX: "hidden", overflowY: "scroll" }}
          >
            <div className="col-11 m-auto">
              <div className="col-12">
                <div className="col-12 py-3">
                  <button
                    className="btn btn-danger px-3 rounded-4 mb-3 mt-3"
                    onClick={() => setShowSpecific(0)}
                  >
                    {t("Back")}
                  </button>
                </div>

                <div className="col-11 my-4 mx-auto">
                  <div className="row">
                    <div className="col-9 my-auto">
                      <h2
                        className="col-10 m-0"
                        style={{ color: "#2C78DB", fontWeight: "700", fontSize: "1.4rem" }}
                      >
                        {t("Deduction Data of Bill")} {selectedDemandID}
                      </h2>
                    </div>
                  </div>
                </div>

                <div className="col-12 pb-5">
                  <div
                    className="m-auto mb-3"
                    style={{
                      backgroundColor: "white", padding: "40px 40px",
                      boxShadow: "0px 0px 5px 1px #E4E8EC", borderRadius: "20px",
                      width: "100%", maxWidth: "100%", overflowX: "hidden",
                    }}
                  >
                    <div
                      className="col-12 tableDesign"
                      style={{
                        background: "#2C78DB", overflowX: "auto", overflowY: "auto",
                        maxHeight: "600px", position: "relative",
                      }}
                    >
                      <table className="m-0 p-0" style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead style={{ position: "sticky", backgroundColor: "#1976d2", top: 0, zIndex: 10 }}>
                          <tr style={{ color: "white", border: "none", height: "3rem", fontSize: "18px" }}>
                            <th style={{ width: "10%" }}>{t("Sr No")}</th>
                            <th style={{ width: "45%" }}>{t("Deduction Name")}</th>
                            <th style={{ width: "45%", textAlign: "left" }}>{t("Deduction Amount")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fetchSpecificDemandDeductionDetail && fetchSpecificDemandDeductionDetail.length > 0 ? (
                            <>
                              {fetchSpecificDemandDeductionDetail
                                .filter((item) => item.TypeOfAmount !== "Total Amount Payable")
                                .map((val, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      backgroundColor: index % 2 === 0 ? "#E5F4FB" : "#CFE4F1",
                                      fontSize: "18px",
                                      fontWeight: "400",
                                    }}
                                  >
                                    <td>{index + 1}</td>
                                    <td>{val.TypeOfAmount}</td>
                                    <td style={{ textAlign: "left" }}>
                                      ₹ {Number(val.TotalAmount).toLocaleString("en-IN")}
                                    </td>
                                  </tr>
                                ))}

                              <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0", fontSize: "18px" }}>
                                <td colSpan={2}>{t("Total Deduction Amount")}</td>
                                <td style={{ textAlign: "left" }}>
                                  ₹{" "}
                                  {Number(
                                    fetchSpecificDemandDeductionDetail
                                      .filter((item) => item.TypeOfAmount !== "Total Amount Payable")
                                      .reduce((acc, curr) => acc + parseFloat(curr.TotalAmount || 0), 0),
                                  ).toLocaleString("en-IN")}
                                </td>
                              </tr>
                            </>
                          ) : (
                            <tr>
                              <td colSpan={5} className="fw-bold">
                                {t("No Data Found !!")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= PROGRESS IMAGES MODAL ================= */}
        {showProgressModal && (
          <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Physical Progress Images</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowProgressModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    {progressData?.length > 0 ? (
                      progressData.map((item, index) => (
                        <div className="col-md-4 mb-3" key={index}>
                          <div
                            style={{
                              border: "1px solid #ddd", borderRadius: "10px",
                              padding: "10px", background: "#fff",
                            }}
                          >
                            <img
                              src={`${apiurl}/UploadProgressDocsImages/${item?.image}`}
                              alt="Document"
                              style={{ width: "120px", cursor: "pointer", maxHeight: "250px", objectFit: "contain" }}
                              onClick={() => {
                                setSelectedImageIndex(index);
                                setShowImageModal(true);
                              }}
                            />
                            <div style={{ marginTop: "10px", fontSize: "14px", lineHeight: "1.7" }}>
                              <div>
                                📍 <strong>Latitude:</strong> {item.location?.latitude || "-"}
                              </div>
                              <div>
                                📍 <strong>Longitude:</strong> {item.location?.longitude || "-"}
                              </div>
                              <div>
                                📅 <strong>Date:</strong> {item.location?.date || "-"}
                              </div>
                              <div>
                                🕒 <strong>Time:</strong> {item.location?.time || "-"}
                              </div>
                              <div>
                                ⛰️ <strong>Elevation:</strong> {item.location?.elevation || "-"} m
                              </div>
                              <div>
                                🎯 <strong>Accuracy:</strong> {item.location?.accuracy || "-"} m
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p>No Images Found</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= IMAGE PREVIEW ================= */}
        {showImageModal && (
          <div
            onClick={() => setShowImageModal(false)}
            style={{
              position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
              backgroundColor: "rgba(0,0,0,0.8)", display: "flex",
              justifyContent: "center", alignItems: "center", zIndex: 9999,
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex((selectedImageIndex - 1 + progressData.length) % progressData.length);
              }}
              style={{
                position: "absolute", left: "20px", top: "50%", transform: "translateY(-50%)",
                fontSize: "30px", background: "rgba(0,0,0,0.5)", color: "#fff", border: "none",
                borderRadius: "50%", width: "50px", height: "50px", cursor: "pointer", zIndex: 10001,
              }}
            >
              ❮
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowImageModal(false);
              }}
              style={{
                position: "absolute", top: "20px", right: "30px", fontSize: "30px",
                color: "#fff", background: "transparent", border: "none", cursor: "pointer", zIndex: 10001,
              }}
            >
              ×
            </button>

            <img
              src={`${apiurl}/UploadProgressDocsImages/${progressData[selectedImageIndex]?.image}`}
              alt="Preview"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "85%", maxHeight: "85%", borderRadius: "8px", objectFit: "contain" }}
            />

            <div
              style={{
                position: "absolute", bottom: "20px", color: "#fff", fontSize: "16px",
                fontWeight: "bold", background: "rgba(0,0,0,0.5)", padding: "6px 12px", borderRadius: "20px",
              }}
            >
              {selectedImageIndex + 1} / {progressData.length}
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex((selectedImageIndex + 1) % progressData.length);
              }}
              style={{
                position: "absolute", right: "20px", top: "50%", transform: "translateY(-50%)",
                fontSize: "30px", background: "rgba(0,0,0,0.5)", color: "#fff", border: "none",
                borderRadius: "50%", width: "50px", height: "50px", cursor: "pointer", zIndex: 10001,
              }}
            >
              ❯
            </button>
          </div>
        )}

        {/* ================= LOADER ================= */}
        {loading && (
          <div
            style={{
              position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
              background: "rgba(255,255,255,0.4)", backdropFilter: "blur(2px)",
              display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999,
            }}
          >
            <div className="Adminloader"></div>
          </div>
        )}
      </div>
    </>
  );
};

export default ViewDemandGeneration;


//--> old ocde
// import React, { useState, useEffect, useContext, useRef } from "react";
// import AdminContext from "../../../StateContext/Application";
// // import { apiurl } from '../../../../path';
// import { Link } from "react-router-dom";
// // import "./rejectpanel.css";
// import {
//   Back32,
//   Cancel32,
//   ProposalCreation32,
//   submit,
// } from "../../../../../public/LoginImages/importimages";
// import { apiurl } from "../../../../../path";
// import { useLanguage } from "../../../../../lang/langcontext/LanguageContext";
// import * as XLSX from "xlsx";
// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";
// import GooglePagination from "../../../styles/GooglePagination";
// import { useNavigate } from "react-router-dom";
// import { formatDateTime, formatDate } from "../../../../../utils/DateFormat";

// const ViewDemandGeneration = () => {
//   const navigate = useNavigate();
//   const { language, toggleLanguage, t } = useLanguage();
//   const context = useContext(AdminContext);
//   const { GetBudgetHeadInfo, TechnicalOutwards, Usermenu, Getyears } = context;

//   const [currentuser, setcurrentUser] = useState();
//   const [currentUserID, setCurrentUserID] = useState();
//   const [selectedImage, setSelectedImage] = useState("");
//   const [progressData, setProgressData] = useState([]);
//   const [showProgressModal, setShowProgressModal] = useState(false);
//   const [showImageModal, setShowImageModal] = useState(false);
//   const [selectedImageIndex, setSelectedImageIndex] = useState(0);
//   const [technicalInwards, setTechnicalInwards] = useState([]);
//   const [selectedBills, setSelectedBills] = useState([]);
//   const [filteredBills, setFilteredBills] = useState([]);
//   const [selectedProposal, setSelectedProposal] = useState(null);
//   const [proposallist, setProposalList] = useState([]);
//   const [SelectedTaluka, setSelectedTaluka] = useState([]);
//   const [selectedWorkOrderType, setSelectedWorkOrderType] = useState("");
//   const [selectedHeadCode, setSelectedHeadCode] = useState("");
//   const [openTaluka, setOpenTaluka] = useState(false);
//   const [loginDistrict, setLoginDistrict] = useState("");
//   const [totalBillsCount, setTotalBillsCount] = useState(0);
  

//   const [UserRole, setUserRole] = useState("");
//   const [AllDistrict, setAllDistrict] = useState([]);
//   const [TalukasofDistrict, setTalukasofDistrict] = useState([]);
//   const [SelectedDistrict, setSelectedDistrict] = useState([]);
//   // const [SelectedTalukasofDistrict, setSelectedTalukasofDistrict] = useState("");
//   const [statusofData, setstatusofData] = useState("Sent to Bank");
//   const [statusofData1, setstatusofData1] = useState("");
//   const [RoleID, setroleID] = useState(null);
//   const [typeofData, settypeofData] = useState("District");
//   const [openDistrict, setOpenDistrict] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [showAll, setShowAll] = useState(false);
//   const billsPerPage = showAll ? filteredBills.length : 10;
//   const [searchTerm, setSearchTerm] = useState("");
//   const budgetEstimation = proposallist;
//   const totalPages = Math.ceil(filteredBills?.length / billsPerPage);

  
//   const indexOfLastBill = currentPage * billsPerPage;
//   const indexOfFirstBill = indexOfLastBill - billsPerPage;
//   const currentBills = filteredBills?.slice(indexOfFirstBill, indexOfLastBill);

//   const [exportingPDF, setExportingPDF] = useState(false);
//   const [loading, setloading] = useState(false);
//   const displayedGRS = exportingPDF ? filteredBills : currentBills;
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");
//   const [approvedYear, setApprovedYear] = useState("");

//   const [showspecific, setShowSpecific] = useState(0);

//   const [selectedBillData, setselectedBillData] = useState({});

//   const [selectedDemandID, setSelectedDemandID] = useState();

//   const [
//     fetchSpecificDemandDeductionDetail,

//     setfetchSpecficDemandDeductionDetail,
//   ] = useState([]);

//   const billSlipRef = useRef(null);

//   const [exportingBillSlipPDF, setExportingBillSlipPDF] = useState(false);

//   const [userDistrict, setuserDistrict] = useState({});

//   const headerStyle = {
//     padding: "12px 16px",
//     whiteSpace: "nowrap",
//     borderRight: "1px solid #e0e0e0",
//     textTransform: "capitalize",
//   };
//   const commonWidth = `${100 / 7}%`; // whatever the current width of Department Auditor looks like

//   const headerStyle1 = {
//     width: commonWidth,
//     padding: "12px 16px",
//     whiteSpace: "nowrap",
//     borderRight: "1px solid #e0e0e0",
//     textTransform: "capitalize",
//   };

//   const cellStyle = {
//     width: commonWidth, // 100% / 7 columns
//     padding: "8px",
//     textAlign: "center",
//     //   whiteSpace: 'nowrap'
//   };

//   const getTechnicalInwardsList = async () => {
//     let data = await GetBudgetHeadInfo("All", "GetDemandGenerationList");
//     let data1 = await TechnicalOutwards("Bill");
//     const { status, message, Tabletype } = data1;

//     if (status) {
//       setTechnicalInwards(message);
//       setProposalList(message);
//       // setFilteredBills(message);
//       setUserRole(Tabletype);
//     } else {
//       alert(message);
//     }
//   };
//   const getmenu = async () => {
//     try {
//       const data = await Usermenu();
//       if (!data || typeof data !== "object") {
//         console.error("Invalid data:", data);
//         return;
//       }
//       const roleID = data?.Data?.RoleID;
//       setroleID(roleID);
//       setuserDistrict(data?.Data.District);
//       setLoginDistrict(data?.Data.District);
//       setcurrentUser(data.Data.FullName);
//       setCurrentUserID(data.Data.ID);
//       console.log("sffdsf", data?.Data);
//     } catch (error) {
//       console.error("Error in AdminNavbar getmenu:", error);
//     }
//   };
//   useEffect(() => {
//     getTechnicalInwardsList();
//     getmenu();
//   }, []);

//   const allDistricts = [
//     ...new Set(
//       AllDistrict?.map((d) => {
//         const district = d.District_Marathi || "";
//         return district.split("-")[0].trim();
//       })
//     ),
//   ].filter(Boolean);

//   const isAllSelected = SelectedDistrict.length === allDistricts.length;

//   const toggleAllDistricts = () => {
//     setSelectedDistrict(isAllSelected ? [] : allDistricts);
//   };
//   const handleDistrictToggle = (district) => {
//     setSelectedDistrict((prev) =>
//       prev.includes(district)
//         ? prev.filter((d) => d !== district)
//         : [...prev, district],
//     );
//   };

//   // 🔹 ULBs depend on the selected District(s); if no district is
//   // selected yet, show ULBs for all districts.
//  const allTalukas = [
//   ...new Set(
//     (Array.isArray(technicalInwards) ? technicalInwards : [])
//       .filter((item) => {
//         if (SelectedDistrict.length === 0) {
//           return true;
//         }

//         const itemDistrict =
//           item.District || item.District_Marathi || "";

//         return SelectedDistrict.some(
//           (district) =>
//             itemDistrict === district ||
//             itemDistrict.startsWith(`${district}-`)
//         );
//       })
//       .map(
//         (item) =>
//           item.ulb ||
//           item.Taluka ||
//           item.ULBNameMarathi
//       ),
//   ),
// ].filter(Boolean);

//   const isAllTalukaSelected =
//     allTalukas.length > 0 && SelectedTaluka.length === allTalukas.length;

//   const toggleAllTalukas = () => {
//     setSelectedTaluka(isAllTalukaSelected ? [] : allTalukas);
//   };

//   const handleTalukaToggle = (taluka) => {
//     setSelectedTaluka((prev) =>
//       prev.includes(taluka)
//         ? prev.filter((t) => t !== taluka)
//         : [...prev, taluka],
//     );
//   };

//   useEffect(() => {
//     setSelectedTaluka([]);
//   }, [SelectedDistrict]);

//   useEffect(() => {
//     let filtered = [...technicalInwards];
//     console.log("All data ---> ", technicalInwards);
//     if (approvedYear) {
//       filtered = filtered.filter(
//         (item) => String(item.WorkApprovedYear) === String(approvedYear),
//       );
//     }
//     if (typeofData === "District" && RoleID === 29) {
//       filtered = filtered.filter(
//         (item) =>
//           item.BudgetID ===
//           "3054-2419(Roads and bridge repair maintenance program)",
//       );
//       console.log("filtered Date 3054", filtered);
//     } else if (typeofData === "State" && RoleID === 29) {
//       filtered = filtered.filter(
//         (item) => item.BudgetID === "2515-1238(MLA Representative)",
//       );
//     }
//    // 🔹 Filter by District (MULTI-SELECT)
//     if (SelectedDistrict?.length > 0) {
//   filtered = filtered.filter((item) =>
//     SelectedDistrict.some((district) => {
//       const itemDistrict = item.District || item.District_Marathi || "";

//       return (
//         itemDistrict === district ||
//         itemDistrict.startsWith(`${district}-`)
//       );
//     })
//   );
// }

//     // 🔹 Filter by Taluka (MULTI-SELECT)
//     if (SelectedTaluka?.length > 0) {
//       filtered = filtered.filter((item) =>
//         SelectedTaluka.includes(item.ulb || item.ULBNameMarathi || item.Taluka),
//       );
//     }

//     // 🔹 Filter by Work Order Type
//     if (selectedWorkOrderType) {
//       filtered = filtered.filter(
//         (item) =>
//           (item.WorkOrderType || item.workOrderType) === selectedWorkOrderType,
//       );
//     }

//     // 🔹 Filter by Head Code
//     if (selectedHeadCode) {
//       filtered = filtered.filter((item) => item.BudgetID === selectedHeadCode);
//     }

//     // 🔹 Filter by Approval / Status
//     if (statusofData1) {
//       if (statusofData1 === "false") {
//         filtered = filtered.filter(
//           (item) =>
//             item.Approvance1 === "false" ||
//             item.Approvance2 === "false" ||
//             item.Approvance3 === "false" ||
//             item.Approvance4 === "false" ||
//             item.Approvance5 === "false" ||
//             item.Approvance6 === "false" ||
//             item.Approvance9 === "false" ||
//             item.Approvance8 === "false" ||
//             item.Approvance9 === "false",
//         );
//       } else if (statusofData1 === "Approved") {
//         // ✅ Added block
//         filtered = filtered.filter(
//           (item) =>
//             item.Approvance1 === "true" &&
//             item.Approvance2 === "true" &&
//             item.Approvance3 === "true" &&
//             item.Approvance4 === "true" &&
//             item.Approvance5 === "true" &&
//             item.Approvance6 === "true" &&
//             item.Approvance9 === "true" &&
//             item.Approvance8 === "true" &&
//             item.Approvance9 === "true",
//         );
//       } else if (statusofData1 === "Rejected") {
//         filtered = filtered.filter((item) => item.Status === "Rejected");
//       } else if (statusofData1 === "SendBack") {
//         filtered = filtered.filter((item) => item.Status === "SendBack");
//       }
//     }

//     console.log("Filter data ---> ", filtered);

//     // 🔹 Search Filter
//     if (searchTerm.trim()) {
//       const lower = searchTerm.toLowerCase();
//       filtered = filtered.filter((bill) =>
//         Object.values(bill).some((val) =>
//           String(val ?? "")
//             .toLowerCase()
//             .includes(lower),
//         ),
//       );
//     }

//     if (fromDate || toDate) {
//       filtered = filtered.filter((item) => {
//         if (!item.Date) return false;

//         // Convert ISO date properly
//         const uploadDate = new Date(item.Date);
//         uploadDate.setHours(0, 0, 0, 0);

//         const from = fromDate ? new Date(fromDate) : null;
//         const to = toDate ? new Date(toDate) : null;

//         if (from) from.setHours(0, 0, 0, 0);
//         if (to) to.setHours(23, 59, 59, 999);

//         if (from && to) {
//           return uploadDate >= from && uploadDate <= to;
//         }

//         if (from) {
//           return uploadDate >= from;
//         }

//         if (to) {
//           return uploadDate <= to;
//         }

//         return true;
//       });
//     }

//     // All filters applied till this point

//     // RoleID 80/81 → always restrict to 2515 records only
//     if (RoleID === 80 || RoleID === 81) {
//       filtered = filtered.filter((item) => item.BudgetID?.startsWith("2515"));
//     }
    
//         // All filters applied till this point

// setTotalBillsCount(filtered.length);

//     // Hide 2515 Jalna/Nanded records by default for District login
// if (
//   (loginDistrict === "जालना" || loginDistrict === "नांदेड") &&
//   RoleID !== 40 &&
//   RoleID !== 41 &&
//   RoleID !== 80 &&
//   RoleID !== 81 &&
//   SelectedDistrict.length === 0 &&
//   !selectedHeadCode
// ) {
//   filtered = filtered.filter((item) => {
//     const is2515 = item.BudgetID?.startsWith("2515");
//     const itemDistrict = item.District || item.District_Marathi || "";

//     const isJalnaNanded =
//       itemDistrict === "जालना" ||
//       itemDistrict.startsWith("जालना-") ||
//       itemDistrict === "नांदेड" ||
//       itemDistrict.startsWith("नांदेड-");

//     return !(is2515 && isJalnaNanded);
//   });
// }

// // Hide 2515 Jalna/Nanded records by default for State 40/41 login

// if (
//   (RoleID === 40 || RoleID === 41) &&
//   SelectedDistrict.length === 0
// ) {
//   filtered = filtered.filter((item) => {
//     const itemDistrict = item.District || item.District_Marathi || "";

//     return !(
//       itemDistrict === "जालना" ||
//       itemDistrict.startsWith("जालना-") ||
//       itemDistrict === "नांदेड" ||
//       itemDistrict.startsWith("नांदेड-")
//     );
//   });
// }
  

//     setFilteredBills(filtered);
//     setCurrentPage(1);
//   }, [
//     statusofData1,
//     searchTerm,
//     technicalInwards,
//     SelectedDistrict,
//     typeofData,
//     fromDate,
//     toDate,
//     approvedYear,
//     SelectedTaluka,
//     selectedWorkOrderType,
//     selectedHeadCode,
//   ]);

//   const selectedBillDetails = (BillID) => {
//     const result = budgetEstimation
//       .filter((item) => item.DemandGenerationID == BillID)
//       .map((item) => ({
//         "Demand ID": item.DemandGenerationID,
//         "RA Bill Number": item.RABillNumber,
//         "Proposal ID": item.ProjectID,
//         "Head ID": item.BudgetID,
//         District: item.District,
//         "Contractor Name": item.ContractorName,
//         "Work Order UID": item.WorkOrderUID,
//         "Upload Demand Copy": item.UploadBillCopy,
//         Remark: item.Remark,
//         "Bill Amount": item.BillAmount,
//         "Net Payable Amount": item.NetPayableAmount,
//         "Deduction Amount": item.DeductionAmount,
//         Date: new Date(item.Date).toLocaleString(),
//       }));

//     console.log("------------------", result);
//     setSelectedProposal(result[0]);
//   };

//   const FetchGramPanachayatInfo = async () => {
//     try {
//       let res = await GetBudgetHeadInfo("null", "AllDistrict");
//       // console.log("Data i get:: ", res);
//       setAllDistrict(res);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   useEffect(() => {
//     FetchGramPanachayatInfo();
//   }, []);

//   const totalBillamt = filteredBills?.reduce((sum, item) => {
//     const amount = Number(item?.BillAmount);
//     return sum + (isNaN(amount) ? 0 : amount);
//   }, 0);
//   const totalpayableamt = filteredBills?.reduce((sum, item) => {
//     const amount = Number(item?.NetPayableAmount);
//     return sum + (isNaN(amount) ? 0 : amount);
//   }, 0);
//   const totaldeductionamt = filteredBills?.reduce((sum, item) => {
//     const amount = Number(item?.DeductionAmount);
//     return sum + (isNaN(amount) ? 0 : amount);
//   }, 0);
//   const pagetotalBillamt = currentBills?.reduce((sum, item) => {
//     const amount = Number(item?.BillAmount);
//     return sum + (isNaN(amount) ? 0 : amount);
//   }, 0);
//   const pagetotalpayableamt = currentBills?.reduce((sum, item) => {
//     const amount = Number(item?.NetPayableAmount);
//     return sum + (isNaN(amount) ? 0 : amount);
//   }, 0);
//   const pagetotaldeductionamt = currentBills?.reduce((sum, item) => {
//     const amount = Number(item?.DeductionAmount);
//     return sum + (isNaN(amount) ? 0 : amount);
//   }, 0);
// const exportTableByClassToExcel = async (
//     bills,
//     filename = "Bills_Outward.xlsx"
// ) => {
//     if (!bills || bills.length === 0) {
//         alert("No data available");
//         return;
//     }

//     setloading(true);

//     await new Promise((resolve) => setTimeout(resolve, 1000));

//     try {
//         const getStatus = (val) =>
//             val === "true"
//                 ? "✓"
//                 : val === "false"
//                     ? "✕"
//                     : "-";

//         const excelData = bills.map((proposal, index) => {
//             const row = {
//                 "Sr.No.": index + 1,

//                 "Demand ID":
//                     proposal.DemandGenerationID || "-",

//                 "RA bill number":
//                     proposal.RABillNumber || "-",

//                 "Class Of Work":
//                     proposal.classOfWork ||
//                     proposal.workClass ||
//                     "-",

//                 "Type Of Work":
//                     proposal.typeOfWork ||
//                     proposal.workType ||
//                     "-",

//                 "Work Order Type":
//                     proposal.WorkOrderType ||
//                     proposal.workOrderType ||
//                     "-",

//                 "Work Name":
//                     proposal.ProjectID || "-",

//                 "District":
//                     proposal.District || "-",

//                 "Taluka":
//                     proposal.ulb ||
//                     proposal.Taluka ||
//                     "-",

//                 "Work Apprved Year":
//                     proposal.WorkApprovedYear || "-",

//                 "Work Apprved GR":
//                     proposal.GRApproveDate ||
//                     proposal.workApprovedGRDate
//                         ? new Date(
//                             proposal.GRApproveDate ||
//                             proposal.workApprovedGRDate
//                         ).toLocaleDateString("en-GB")
//                         : "-",

//                 "Technical Sanction Number":
//                     proposal.BillNumber || "-",
//             };


//             if (UserRole !== "State" || RoleID === 29) {
//                 row["Head ID"] =
//                     proposal.BudgetID || "-";
//             }

//             Object.assign(row, {
//                 "Job ID":
//                     proposal.JOB_ID || "-",

//                 "Physical Progress(in %)":
//                     proposal.physicalValueOfProgress || "-",

//                 "Vendor Name":
//                     proposal.ContractorName || "-",

//                 "Bill Copy":
//                     proposal.UploadBillCopy || "No File",

//                 "Measurement Book":
//                     proposal.Measurement_Book || "No File",

//                 "SQM Inspection Book":
//                     proposal.Inspection_Report || "No File",

//                 "Work completion Certificate Book":
//                     proposal.Work_completion || "No File",

//                 "Administrative Approved GR Copy":
//                     proposal.GRCopy || "No File",

//                 "Remark":
//                     proposal.Remark || "-",

//                 "Reject/SentBack Remark":
//                     proposal.Remark || "-",

//                 "Bill Amount":
//                     proposal.BillAmount || 0,

//                 "Net Payable Amount":
//                     proposal.NetPayableAmount || 0,

//                 "Deduction Amount":
//                     proposal.DeductionAmount || 0,

//                 "Creation Date":
//                     proposal.Date
//                         ? new Date(
//                             proposal.Date
//                         ).toLocaleString("en-IN")
//                         : "-",

//                 "Upload Demand Copy":
//                     proposal.UploadBillCopy || "No File",

//                 "Demand Copy":
//                     proposal.BillCopy || "No File",

//                 "Action By":
//                     proposal.Status === "SendBack"
//                         ? `Send Back By - ${
//                             proposal.ActionBy === "NULL"
//                                 ? "-"
//                                 : proposal.ActionBy || "-"
//                         }`
//                         : proposal.Status === "Rejected"
//                             ? `Rejected By - ${
//                                 proposal.ActionBy === "NULL"
//                                     ? "-"
//                                     : proposal.ActionBy || "-"
//                             }`
//                             : proposal.ActionBy === "NULL"
//                                 ? "-"
//                                 : proposal.ActionBy || "-",

//                 "Department Auditor":
//                     getStatus(proposal.Approvance1),

//                 "Department HOD(EE)":
//                     getStatus(proposal.Approvance2),

//                 "FD Auditor":
//                     getStatus(proposal.Approvance3),

//                 "FD AAO":
//                     getStatus(proposal.Approvance4),

//                 "Deputy CAFO":
//                     getStatus(proposal.Approvance6),

//                 "CAFO":
//                     getStatus(proposal.Approvance7),

//                 "RDD":
//                     getStatus(proposal.Approvance9),
//             });


//             if (
//                 RoleID === 14 ||
//                 RoleID === 31 ||
//                 RoleID === 80
//             ) {
//                 row["Action"] =
//                     proposal.Status || "-";
//             }

//             return row;
//         });

//         const totalBillAmount = bills.reduce(
//             (sum, bill) =>
//                 sum + Number(bill.BillAmount || 0),
//             0
//         );

//         const totalNetPayableAmount = bills.reduce(
//             (sum, bill) =>
//                 sum + Number(bill.NetPayableAmount || 0),
//             0
//         );

//         const totalDeductionAmount = bills.reduce(
//             (sum, bill) =>
//                 sum + Number(bill.DeductionAmount || 0),
//             0
//         );

//         const totalRow = {
//             "Sr.No.": "",

//             "Demand ID": "",
//             "RA bill number": "",
//             "Class Of Work": "",
//             "Type Of Work": "",
//             "Work Order Type": "",
//             "Work Name": "",
//             "District": "",
//             "Taluka": "",
//             "Work Apprved Year": "",
//             "Work Apprved GR": "",
//             "Technical Sanction Number": "",
//         };

//         if (UserRole !== "State" || RoleID === 29) {
//             totalRow["Head ID"] = "";
//         }


//         Object.assign(totalRow, {
//             "Job ID": "",

//             "Physical Progress(in %)": "",

//             "Vendor Name": "",

//             "Bill Copy": "",

//             "Measurement Book": "",

//             "SQM Inspection Book": "",

//             "Work completion Certificate Book": "",

//             "Administrative Approved GR Copy": "",

//             "Remark": "",

//             "Reject/SentBack Remark": "Total",

//             // TOTAL AMOUNTS
//             "Bill Amount": totalBillAmount,

//             "Net Payable Amount":
//                 totalNetPayableAmount,

//             "Deduction Amount":
//                 totalDeductionAmount,

//             "Creation Date": "",

//             "Upload Demand Copy": "",

//             "Demand Copy": "",

//             "Action By": "",

//             "Department Auditor": "",

//             "Department HOD(EE)": "",

//             "FD Auditor": "",

//             "FD AAO": "",

//             "Deputy CAFO": "",

//             "CAFO": "",

//             "RDD": "",
//         });

//         if (
//             RoleID === 14 ||
//             RoleID === 31 ||
//             RoleID === 80
//         ) {
//             totalRow["Action"] = "";
//         }


//         excelData.push(totalRow);

//         const worksheet =
//             XLSX.utils.json_to_sheet(excelData);


//         worksheet["!cols"] =
//             Object.keys(excelData[0]).map(
//                 (column) => {
//                     if (column === "Sr.No.") {
//                         return { wch: 12 };
//                     }

//                     if (
//                         column === "Work Name" ||
//                         column === "Remark" ||
//                         column ===
//                             "Reject/SentBack Remark"
//                     ) {
//                         return { wch: 30 };
//                     }

//                     return { wch: 22 };
//                 }
//             );


//         worksheet["!freeze"] = {
//             xSplit: 0,
//             ySplit: 1,
//         };


//         worksheet["!autofilter"] = {
//             ref: XLSX.utils.encode_range({
//                 s: {
//                     r: 0,
//                     c: 0,
//                 },
//                 e: {
//                     r: excelData.length - 2,
//                     c:
//                         Object.keys(
//                             excelData[0]
//                         ).length - 1,
//                 },
//             }),
//         };


//         const workbook =
//             XLSX.utils.book_new();

//         XLSX.utils.book_append_sheet(
//             workbook,
//             worksheet,
//             "Bills"
//         );


//         XLSX.writeFile(
//             workbook,
//             filename
//         );
//     } catch (error) {
//         console.error(
//             "Excel Export Error:",
//             error
//         );
//     } finally {
//         setloading(false);
//     }
// };
  
//   const downloadTableOnlyAsPDF = async (
//     tableClass,
//     filename,
//     title,
//     chunkSize = 500
// ) => {
//     const originalTable = document.querySelector(`.${tableClass}`);

//     if (!originalTable) {
//         setExportingPDF(false);
//         return;
//     }

//     setloading(true);
//     setExportingPDF(true);

//     await new Promise((resolve) => setTimeout(resolve, 600));

//     try {
//         const originalRows = Array.from(
//             originalTable.querySelectorAll("tbody tr")
//         );

//         if (!originalRows.length) {
//             setloading(false);
//             setExportingPDF(false);
//             return;
//         }

//         /*
//          * ---------------------------------------------------------
//          * Identify summary rows
//          * ---------------------------------------------------------
//          * Your table has 2 rows at the bottom:
//          * 1. Page Total
//          * 2. Gross Total
//          *
//          * We will keep only the LAST summary row (Gross Total)
//          * and rename it to "Total".
//          */

//         const SUMMARY_ROW_COUNT = 2;

//         let dataRows = originalRows;
//         let totalRow = null;

//         if (originalRows.length >= SUMMARY_ROW_COUNT) {
//             const summaryRows = originalRows.slice(-SUMMARY_ROW_COUNT);

//             // Keep only the last row = Gross Total
//             totalRow = summaryRows[summaryRows.length - 1];

//             // Actual data rows
//             dataRows = originalRows.slice(
//                 0,
//                 originalRows.length - SUMMARY_ROW_COUNT
//             );
//         }

//         const totalDataRows = dataRows.length;

//         /*
//          * ---------------------------------------------------------
//          * Generate PDF chunks
//          * ---------------------------------------------------------
//          */

//         for (
//             let start = 0;
//             start < totalDataRows;
//             start += chunkSize
//         ) {
//             const end = Math.min(
//                 start + chunkSize,
//                 totalDataRows
//             );

//             const isLastChunk = end === totalDataRows;

//             // Clone table
//             const tableClone = originalTable.cloneNode(true);

//             const rows = Array.from(
//                 tableClone.querySelectorAll("tbody tr")
//             );

//             /*
//              * Remove ALL rows first.
//              * We will add only the required data rows.
//              */
//             rows.forEach((row) => row.remove());

//             const tbody = tableClone.querySelector("tbody");

//             /*
//              * -----------------------------------------------------
//              * Add current chunk data rows
//              * -----------------------------------------------------
//              */

//             const chunkRows = dataRows.slice(start, end);

//             chunkRows.forEach((originalRow, index) => {
//                 const rowClone = originalRow.cloneNode(true);

//                 const firstCell = rowClone.querySelector("td");

//                 if (firstCell) {
//                     // Correct Sr No across chunks
//                     firstCell.textContent = start + index + 1;
//                 }

//                 tbody.appendChild(rowClone);
//             });

//             /*
//              * -----------------------------------------------------
//              * Add ONLY ONE Total row
//              * -----------------------------------------------------
//              *
//              * Add it only on the final PDF chunk.
//              */

//             if (isLastChunk && totalRow) {
//                 const totalRowClone = totalRow.cloneNode(true);

//                 const cells = totalRowClone.querySelectorAll("td");

//                 if (cells.length > 0) {
//                     // First cell should say Total
//                     cells[0].textContent = "Total";
//                 }

//                 tbody.appendChild(totalRowClone);
//             }

//             /*
//              * Hide cloned table
//              */
//             tableClone.style.position = "absolute";
//             tableClone.style.left = "-9999px";
//             tableClone.style.top = "0";

//             document.body.appendChild(tableClone);

//             const pdf = new jsPDF("p", "mm", "a4");

//             const pageWidth =
//                 pdf.internal.pageSize.getWidth();

//             const pageHeight =
//                 pdf.internal.pageSize.getHeight();

//             const topMargin = 25;
//             const sideMargin = 10;
//             const bottomMargin = 15;

//             /*
//              * -----------------------------------------------------
//              * PDF Title
//              * -----------------------------------------------------
//              */

//             pdf.setFontSize(16);
//             pdf.setFont("helvetica", "bold");

//             pdf.text(
//                 `${title} (${start + 1} - ${end})`,
//                 pageWidth / 2,
//                 15,
//                 {
//                     align: "center",
//                 }
//             );

//             /*
//              * -----------------------------------------------------
//              * Convert table to canvas
//              * -----------------------------------------------------
//              */

//             const canvas = await html2canvas(
//                 tableClone,
//                 {
//                     scale: 1,
//                     useCORS: true,
//                     backgroundColor: "#2C78DB",
//                 }
//             );

//             const imgWidth =
//                 pageWidth - sideMargin * 2;

//             const pxToMm =
//                 imgWidth / canvas.width;

//             const usablePageHeightMm =
//                 pageHeight -
//                 topMargin -
//                 bottomMargin;

//             const usablePageHeightPx =
//                 usablePageHeightMm / pxToMm;

//             let renderedHeight = 0;
//             let pageNumber = 0;

//             /*
//              * -----------------------------------------------------
//              * Split canvas across PDF pages
//              * -----------------------------------------------------
//              */

//             while (renderedHeight < canvas.height) {
//                 const sliceHeight = Math.min(
//                     usablePageHeightPx,
//                     canvas.height - renderedHeight
//                 );

//                 const pageCanvas =
//                     document.createElement("canvas");

//                 const context =
//                     pageCanvas.getContext("2d");

//                 pageCanvas.width = canvas.width;
//                 pageCanvas.height = sliceHeight;

//                 context.drawImage(
//                     canvas,
//                     0,
//                     renderedHeight,
//                     canvas.width,
//                     sliceHeight,
//                     0,
//                     0,
//                     canvas.width,
//                     sliceHeight
//                 );

//                 const imgData =
//                     pageCanvas.toDataURL(
//                         "image/jpeg",
//                         0.85
//                     );

//                 if (pageNumber > 0) {
//                     pdf.addPage();
//                 }

//                 pdf.addImage(
//                     imgData,
//                     "JPEG",
//                     sideMargin,
//                     topMargin,
//                     imgWidth,
//                     sliceHeight * pxToMm
//                 );

//                 renderedHeight += sliceHeight;
//                 pageNumber++;
//             }

//             /*
//              * -----------------------------------------------------
//              * Save PDF
//              * -----------------------------------------------------
//              */

//             pdf.save(
//                 `${filename}_${start + 1}_to_${end}.pdf`
//             );

//             /*
//              * Remove temporary table
//              */

//             document.body.removeChild(tableClone);

//             await new Promise((resolve) =>
//                 setTimeout(resolve, 300)
//             );
//         }
//     } catch (error) {
//         console.error(
//             "PDF Export Error:",
//             error
//         );
//     } finally {
//         setExportingPDF(false);
//         setloading(false);
//     }
// };


//   //--> new code

//   // Added New for start index 1
// // const downloadTableOnlyAsPDF = async (
// //   tableClass,
// //   filename,
// //   title,
// //   chunkSize = 500
// // ) => {
// //   const originalTable = document.querySelector(`.${tableClass}`);

// //   if (!originalTable) {
// //     setExportingPDF(false);
// //     return;
// //   }

// //   setloading(true);
// //   setExportingPDF(true);

// //   // Allow React to render all records
// //   await new Promise((resolve) => setTimeout(resolve, 600));

// //   try {
// //     const originalRows = originalTable.querySelectorAll("tbody tr");
// //     const totalRows = originalRows.length;

// //     if (!totalRows) {
// //       setloading(false);
// //       setExportingPDF(false);
// //       return;
// //     }

// //     for (let start = 0; start < totalRows; start += chunkSize) {
// //       const end = Math.min(start + chunkSize, totalRows);

// //       // Clone table
// //       const tableClone = originalTable.cloneNode(true);

// //       const rows = tableClone.querySelectorAll("tbody tr");

// //       // Remove rows outside current chunk
// //       rows.forEach((row, index) => {
// //         if (index < start || index >= end) {
// //           row.remove();
// //         }
// //       });

// //       // =====================================================
// //       // 🔥 IMPORTANT FIX
// //       // Reset Sr.No. inside PDF clone only
// //       // =====================================================

// //       const pdfRows = tableClone.querySelectorAll("tbody tr");

// //       pdfRows.forEach((row, index) => {
// //         const firstCell = row.querySelector("td");

// //         if (firstCell) {
// //           firstCell.textContent = start + index + 1;
// //         }
// //       });

// //       // Hide cloned table
// //       tableClone.style.position = "absolute";
// //       tableClone.style.left = "-9999px";
// //       tableClone.style.top = "0";

// //       document.body.appendChild(tableClone);

// //       const pdf = new jsPDF("p", "mm", "a4");

// //       const pageWidth = pdf.internal.pageSize.getWidth();
// //       const pageHeight = pdf.internal.pageSize.getHeight();

// //       const topMargin = 25;
// //       const sideMargin = 10;
// //       const bottomMargin = 15;

// //       // PDF title
// //       pdf.setFontSize(16);
// //       pdf.setFont("helvetica", "bold");

// //       pdf.text(
// //         `${title} (${start + 1} - ${end})`,
// //         pageWidth / 2,
// //         15,
// //         {
// //           align: "center",
// //         }
// //       );

// //       // Convert table to canvas
// //       const canvas = await html2canvas(tableClone, {
// //         scale: 1,
// //         useCORS: true,
// //         backgroundColor: "#2C78DB",
// //       });

// //       const imgWidth = pageWidth - sideMargin * 2;

// //       const pxToMm = imgWidth / canvas.width;

// //       const usablePageHeightMm =
// //         pageHeight - topMargin - bottomMargin;

// //       const usablePageHeightPx =
// //         usablePageHeightMm / pxToMm;

// //       let renderedHeight = 0;
// //       let pageNumber = 0;

// //       while (renderedHeight < canvas.height) {
// //         const sliceHeight = Math.min(
// //           usablePageHeightPx,
// //           canvas.height - renderedHeight
// //         );

// //         const pageCanvas = document.createElement("canvas");
// //         const context = pageCanvas.getContext("2d");

// //         pageCanvas.width = canvas.width;
// //         pageCanvas.height = sliceHeight;

// //         context.drawImage(
// //           canvas,
// //           0,
// //           renderedHeight,
// //           canvas.width,
// //           sliceHeight,
// //           0,
// //           0,
// //           canvas.width,
// //           sliceHeight
// //         );

// //         const imgData = pageCanvas.toDataURL(
// //           "image/jpeg",
// //           0.85
// //         );

// //         if (pageNumber > 0) {
// //           pdf.addPage();
// //         }

// //         pdf.addImage(
// //           imgData,
// //           "JPEG",
// //           sideMargin,
// //           topMargin,
// //           imgWidth,
// //           sliceHeight * pxToMm
// //         );

// //         renderedHeight += sliceHeight;
// //         pageNumber++;
// //       }

// //       pdf.save(
// //         `${filename}_${start + 1}_to_${end}.pdf`
// //       );

// //       // Remove temporary clone
// //       document.body.removeChild(tableClone);

// //       // Small delay
// //       await new Promise((resolve) =>
// //         setTimeout(resolve, 300)
// //       );
// //     }
// //   } catch (error) {
// //     console.error("PDF Export Error:", error);
// //   } finally {
// //     setExportingPDF(false);
// //     setloading(false);
// //   }
// // };

//   //////// Added for demand id
//   const DemandDeductionDetailByBillID = async (ID) => {
//     try {
//       let res = await Getyears("FetchDemandDeductionDetailbyBillID", ID);
//       setfetchSpecficDemandDeductionDetail(res);
//       setSelectedDemandID(ID);
//       setShowSpecific(5);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   const settingshowspecific3 = async (ID) => {
//     try {
//       let res = await Getyears("FetchDemandDeductionDetailbyBillID", ID);

//       setfetchSpecficDemandDeductionDetail(res);

//       setSelectedDemandID(ID);

//       setShowSpecific(4);

//       let filtered = res;

//       console.log("filtered............", filtered);

//       filtered = filtered.filter((val) => val.DemandGenerationID == ID);

//       setselectedBillData(filtered[0]);

//       console.log("selected Bill ---> ", filtered[0]);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   const exportBillSlipPDF = async () => {
//     if (!billSlipRef.current) {
//       alert("Bill slip not found");

//       return;
//     }

//     setExportingBillSlipPDF(true);

//     try {
//       const canvas = await html2canvas(billSlipRef.current, {
//         scale: 2,

//         useCORS: true,

//         backgroundColor: "#ffffff",
//       });

//       const pdf = new jsPDF("p", "mm", "a4");

//       const pageWidth = pdf.internal.pageSize.getWidth();

//       const pageHeight = pdf.internal.pageSize.getHeight();

//       const margin = 5;

//       const maxImgWidth = pageWidth - margin * 2;

//       const maxImgHeight = pageHeight - margin * 2;

//       const widthRatio = maxImgWidth / canvas.width;

//       const heightRatio = maxImgHeight / canvas.height;

//       const ratio = Math.min(widthRatio, heightRatio);

//       const imgWidth = canvas.width * ratio;

//       const imgHeight = canvas.height * ratio;

//       pdf.addImage(
//         canvas.toDataURL("image/png"),

//         "PNG",

//         (pageWidth - imgWidth) / 2,

//         margin,

//         imgWidth,

//         imgHeight,
//       );

//       const transactionNo =
//         selectedBillData?.DemandGenerationID || selectedDemandID;

//       pdf.save(`Bill_Slip_${transactionNo || "RDD"}.pdf`);
//     } catch (error) {
//       console.error("Bill slip PDF export error:", error);

//       alert("Failed to export bill slip PDF. Please try again.");
//     } finally {
//       setExportingBillSlipPDF(false);
//     }
//   };

//   const BackFunction = () => {
//     setfetchSpecficDemandDeductionDetail([]);

//     setSelectedBills("");

//     setselectedBillData([]);

//     setShowSpecific(0);
//   };

//   const labelStyle = {
//     border: "1px solid #000",

//     padding: "6px",

//     width: "18%",

//     fontWeight: "bold",

//     background: "#f5f5f5",
//   };

//   const valueStyle = {
//     border: "1px solid #000",

//     padding: "6px",
//   };

//   const tableHead = {
//     border: "1px solid #000",

//     background: "#d9d9d9",

//     padding: "8px",

//     textAlign: "center",
//   };

//   const tableCell = {
//     border: "1px solid #000",

//     padding: "8px",

//     textAlign: "center",
//   };

//   const vendorData = fetchSpecificDemandDeductionDetail?.find(
//     (item) => item.TypeOfAmount === "Total Amount Payable",
//   );

//         // Remove duplicate rows based on a combination of key fields
//       const uniqueBy = (arr = [], keyFn) => {
//         const seen = new Set();
//         return arr.filter((item) => {
//           const key = keyFn(item);
//           if (seen.has(key)) return false;
//           seen.add(key);
//           return true;
//         });
//       };
//       // Vendor rows (Total Amount Payable)
//       const uniqueVendorRows = uniqueBy(
//         (fetchSpecificDemandDeductionDetail || []).filter(
//           (val) => val.TypeOfAmount === "Total Amount Payable"
//         ),
//         (v) =>
//           `${v.Vendor_Company_Name}|${v.Bank_IFSC_Code}|${v.Bank_Account_Number}|${v.TotalAmount}`
//       );
//       // Deduction rows (everything except Total Amount Payable)
//       const uniqueDeductionRows = uniqueBy(
//         (fetchSpecificDemandDeductionDetail || []).filter(
//           (val) => val.TypeOfAmount !== "Total Amount Payable"
//         ),
//         (d) =>
//           `${d.TypeOfAmount}|${d.TotalAmount}|${d.Bank_Account_Number}|${d.Bank_IFSC_Code}`
//       );

//   const officerStyle = {
//     marginTop: "8px",

//     fontSize: "12px",

//     fontWeight: "bold",

//     textAlign: "center",
//   };

//   const officerMarathiStyle = {
//     fontSize: "11px",

//     textAlign: "center",

//     marginTop: "3px",
//   };

//   const footerTitleStyle = {
//     width: "100%",

//     borderCollapse: "collapse",

//     marginTop: "8px",
//   };

//   const footerTitleTdStyle = {
//     border: "1px solid #555",

//     padding: "6px",

//     textAlign: "center",

//     fontSize: "12px",

//     fontWeight: "bold",
//   };

//   const remarksTableStyle = {
//     width: "100%",

//     borderCollapse: "collapse",
//   };

//   const remarksTableTdStyle = {
//     border: "1px solid #555",

//     padding: "8px",

//     fontSize: "11px",

//     lineHeight: "18px",

//     textAlign: "right",
//   };

//   const instructionTableStyle = {
//     width: "100%",

//     borderCollapse: "collapse",

//     marginTop: "-1px",
//   };

//   const instructionTableTdStyle = {
//     border: "1px solid #555",

//     padding: "10px",

//     fontSize: "11px",

//     lineHeight: "18px",

//     textAlign: "justify",
//   };

//   const signatureTableStyle = {
//     width: "100%",

//     tableLayout: "fixed",

//     borderCollapse: "collapse",

//     marginTop: "-1px",
//   };

//   const signatureTableTdStyle = {
//     border: "1px solid #555",

//     height: "80px",

//     width: "50%",

//     verticalAlign: "bottom",

//     padding: "10px",
//   };

//   const signBoxStyle = {
//     width: "100%",

//     height: "100%",

//     display: "flex",

//     flexDirection: "column",

//     justifyContent: "flex-end",
//   };

//   const signSpaceStyle = {
//     height: "25px",
//   };

//   const signTitleStyle = {
//     fontSize: "12px",

//     fontWeight: "bold",

//     textAlign: "center",
//   };

//   const openProgressModal = (images, location) => {
//     console.log("image and location ", images, location);
//     try {
//       const parsedImages = JSON.parse(images || "[]");
//       const parsedLocations = JSON.parse(location || "[na]");

//       let imageArray = [];

//       parsedImages.forEach((img) => {
//         try {
//           const nested = JSON.parse(img);

//           if (Array.isArray(nested)) {
//             imageArray.push(...nested);
//           } else {
//             imageArray.push(img);
//           }
//         } catch {
//           imageArray.push(img);
//         }
//       });

//       // const combinedData = imageArray.map((image, index) => {
//       //   let locationObj = {};

//       //   try {
//       //     locationObj = JSON.parse(parsedLocations[index] || "{}");
//       //   } catch (err) {
//       //     console.log(err);
//       //   }

//       //   return {
//       //     image,
//       //     location: locationObj,
//       //   };
//       // });
//       const combinedData = imageArray.map((image, index) => {
//         let locationObj = {};

//         const loc = parsedLocations[index];

//         if (loc && loc !== "na" && loc !== "null" && loc !== "") {
//           try {
//             locationObj = JSON.parse(loc);
//           } catch (err) {
//             console.error("Invalid location JSON:", loc, err);
//           }
//         }

//         return {
//           image,
//           location: locationObj,
//         };
//       });
//       console.log("combinedData", combinedData);

//       setProgressData(combinedData);
//       setShowProgressModal(true);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   const formatFileName = (file) => {
//     if (!file) return "";

//     const dotIndex = file.lastIndexOf(".");
//     const extension = dotIndex !== -1 ? file.substring(dotIndex) : "";
//     const lastThree =
//       dotIndex !== -1 ? file.substring(dotIndex - 4, dotIndex) : "";

//     return `${file.substring(0, 10)}........${lastThree}${extension}`;
//   };

//   const isChecker =
//     RoleID === 80 ||
//     RoleID === 81 ||
//     ((RoleID === 40 || RoleID === 41) &&
//       (SelectedDistrict.includes("जालना") ||
//         SelectedDistrict.includes("नांदेड"))) ||
//     ((loginDistrict === "जालना" || loginDistrict === "नांदेड") &&
//       selectedHeadCode?.startsWith("2515"));

// useEffect(() => {
//   const handleKeyDown = (e) => {
//     if (!progressData?.length) return;

//     if (e.key === "ArrowRight") {
//       setSelectedImageIndex((prevIndex) =>
//         (prevIndex + 1) % progressData.length
//       );
//     }

//     if (e.key === "ArrowLeft") {
//       setSelectedImageIndex((prevIndex) =>
//         (prevIndex - 1 + progressData.length) % progressData.length
//       );
//     }

//     // Optional: close image/modal with Escape
//     // if (e.key === "Escape") {
//     //   setSelectedImageIndex(null);
//     // }
//   };

//   window.addEventListener("keydown", handleKeyDown);

//   return () => {
//     window.removeEventListener("keydown", handleKeyDown);
//   };
// }, [progressData]);
//   return (
//     <>
//       <div
//         className="col-12"
//         style={{
//           backgroundColor: "#EFF3F8",
//           height: "89vh",
//           overflowX: "hidden",
//           overflowY: "scroll",
//           position: "relative",
//         }}
//       >
//         {showspecific === 0 && (
//           <div
//             style={{
//               filter: loading ? "blur(4px)" : "none",
//               pointerEvents: loading ? "none" : "auto",
//               transition: "0.2s ease",
//             }}
//           >
//             <div className="col-11 m-auto">
//               <div className="col-12 ">
//                 <div
//                   className="col-12 px-4 mb-4 mt-3"
//                   style={{
//                     filter: loading ? "blur(4px)" : "none",
//                     pointerEvents: loading ? "none" : "auto",
//                     transition: "0.2s ease",
//                   }}
//                 >
//                   {/* ================= ROW 1 : HEADING + DOWNLOAD ================= */}
//                   <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
//                     <h5 className="fw-bold d-flex align-items-center m-0">
//                       <img
//                         src="/GR-Approval64.png"
//                         alt="Technical Approval"
//                         style={{
//                           width: "24px",
//                           height: "24px",
//                           marginRight: "6px",
//                         }}
//                       />
//                       {[24, 25, 29, 40, 41].includes(RoleID)
//                         ? t("Bill Status")
//                         : t("Bill Outward")}
//                     </h5>

//                     <div className="d-flex gap-2">
//                       <button
//                         className="btn btn-success"
//                         onClick={() =>
//                           exportTableByClassToExcel(
//                             filteredBills,
//                             "Bills_Outward.xlsx",
//                           )
//                         }
//                       >
//                         {t("Download Excel")}
//                       </button>

//                       <button
//                         className="btn btn-danger"
//                         onClick={() =>
//                           downloadTableOnlyAsPDF(
//                             "BillsOutwardTable",
//                             "Bills_Outward.pdf",
//                             "Bills Outward Report",
//                           )
//                         }
//                       >
//                         {t("Download PDF")}
//                       </button>
//                     </div>
//                   </div>

//                   {/* ================= ROW 2 : FILTERS + SEARCH ================= */}
//                   <div
//                   style={{
//                     display: "flex",
//                     flexWrap: "wrap",
//                     alignItems: "center",
//                     gap: "10px",
//                     width: "100%",
//                   }}
//                 >
//                   {/* Type of Data */}
//                   {UserRole === "State" && RoleID == 29 && (
//                     <div style={{ width: "150px", flexShrink: 0 }}>
//                       <select
//                         value={typeofData}
//                         onChange={(e) => settypeofData(e.target.value)}
//                         style={{
//                           width: "100%",
//                           height: "40px",
//                           padding: "6px 10px",
//                           border: "1px solid #ccc",
//                           borderRadius: "8px",
//                           fontSize: "14px",
//                           backgroundColor: "#f1f6fc",
//                           whiteSpace: "nowrap",
//                           overflow: "hidden",
//                           textOverflow: "ellipsis",
//                         }}
//                       >
//                         <option value="" disabled>
//                           Choose Data
//                         </option>

//                         <option value="District">
//                           {t("3054-2419(Roads and bridge repair maintenance program)")}
//                         </option>

//                         <option value="State">
//                           {t("2515-1238(MLA Representative)")}
//                         </option>
//                       </select>
//                     </div>
//                   )}

//                   {/* Status */}
//                   <div style={{ width: "150px", flexShrink: 0 }}>
//                     <select
//                       value={statusofData1}
//                       onChange={(e) => setstatusofData1(e.target.value)}
//                       style={{
//                         width: "100%",
//                         height: "40px",
//                         padding: "6px 10px",
//                         border: "1px solid #ccc",
//                         borderRadius: "8px",
//                         fontSize: "14px",
//                         backgroundColor: "#f1f6fc",
//                       }}
//                     >
//                       <option value="">{t("All")}</option>
//                       <option value="Approved">{t("Approved")}</option>
//                       <option value="false">{t("Pending")}</option>
//                       <option value="Rejected">{t("Rejected")}</option>
//                       <option value="SendBack">{t("Sent Back")}</option>
//                     </select>
//                   </div>

                  

//                   {/* District */}
//                   {UserRole === "State" && (
//                     <div style={{ position: "relative", width: "150px", flexShrink: 0 }}>
//                       <div
//                         onClick={() => setOpenDistrict(!openDistrict)}
//                         style={{
//                           width: "100%",
//                           height: "40px",
//                           boxSizing: "border-box",
//                           padding: "9px 10px",
//                           border: "1px solid #ccc",
//                            borderRadius: "8px",
//                           backgroundColor: "#f1f6fc",
//                           cursor: "pointer",
//                           fontSize: "14px",
//                           whiteSpace: "nowrap",
//                           overflow: "hidden",
//                           textOverflow: "ellipsis",
//                         }}
//                       >
//                         {SelectedDistrict.length > 0
//                           ? SelectedDistrict.join(", ")
//                           : "Select District"}
//                       </div>

//                       {openDistrict && (
//                         <div
//                           style={{
//                             position: "absolute",
//                             top: "44px",
//                             left: 0,
//                             width: "150px",
//                             background: "#fff",
//                             border: "1px solid #ccc",
//                             borderRadius: "8px",
//                             boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
//                             zIndex: 1000,
//                           }}
//                         >
//                           <label
//                             style={{
//                               display: "flex",
//                               alignItems: "center",
//                               padding: "8px",
//                               borderBottom: "1px solid #eee",
//                               cursor: "pointer",
//                               fontSize: "13px",
//                             }}
//                           >
//                             <input
//                               type="checkbox"
//                               checked={isAllSelected}
//                               onChange={toggleAllDistricts}
//                               style={{ marginRight: "6px" }}
//                             />
//                             {t("All Districts")}
//                           </label>

//                           <div style={{ maxHeight: "150px", overflowY: "auto" }}>
//                             {allDistricts.map((district, index) => (
//                               <label
//                                 key={index}
//                                 style={{
//                                   display: "flex",
//                                   alignItems: "center",
//                                   padding: "7px 8px",
//                                   cursor: "pointer",
//                                   fontSize: "13px",
//                                 }}
//                               >
//                                 <input
//                                   type="checkbox"
//                                   checked={SelectedDistrict.includes(district)}
//                                   onChange={() => handleDistrictToggle(district)}
//                                   onClick={(e) => e.stopPropagation()}
//                                   style={{ marginRight: "6px" }}
//                                 />
//                                 {district}
//                               </label>
//                             ))}
//                           </div>

//                           <div
//                             style={{
//                               padding: "6px",
//                               textAlign: "right",
//                               borderTop: "1px solid #eee",
//                             }}
//                           >
//                             <button
//                               onClick={() => setOpenDistrict(false)}
//                               style={{
//                                 padding: "4px 10px",
//                                 fontSize: "12px",
//                                 border: "none",
//                                 borderRadius: "6px",
//                                 background: "#0d6efd",
//                                 color: "#fff",
//                                 cursor: "pointer",
//                               }}
//                             >
//                               OK
//                             </button>
//                           </div>
//                         </div>
//                       )}
//                     </div>
//                   )}

//                     {/* Head Code */}
//                   {![24, 25, 40, 41, 80, 81].includes(RoleID) && (
//                     <div style={{ width: "195px", flexShrink: 0 }}>
//                       <select
//                         value={selectedHeadCode}
//                         onChange={(e) => setSelectedHeadCode(e.target.value)}
//                         style={{
//                           width: "100%",
//                           height: "40px",
//                           padding: "6px 10px",
//                           border: "1px solid #ccc",
//                           borderRadius: "8px",
//                           fontSize: "14px",
//                           backgroundColor: "#f1f6fc",
//                           whiteSpace: "nowrap",
//                           overflow: "hidden",
//                           textOverflow: "ellipsis",
//                         }}
//                       >
//                         <option value="">{t("Select Head Code")}</option>

//                         {[
//                           ...new Set(
//                             technicalInwards
//                               ?.map((item) => item.BudgetID)
//                               .filter(Boolean)
//                           ),
//                         ]
//                           .sort()
//                           .map((headCode, index) => (
//                             <option key={index} value={headCode}>
//                               {headCode}
//                             </option>
//                           ))}
//                       </select>
//                     </div>
//                   )}

//                   {/* Taluka */}
                  
//                     <div style={{ position: "relative", width: "150px", flexShrink: 0 }}>
//                       <div
//                         onClick={() => setOpenTaluka(!openTaluka)}
//                         style={{
//                           width: "100%",
//                           height: "40px",
//                           boxSizing: "border-box",
//                           padding: "9px 10px",
//                           border: "1px solid #ccc",
//                           borderRadius: "8px",
//                           backgroundColor: "#f1f6fc",
//                           cursor: "pointer",
//                           fontSize: "14px",
//                           whiteSpace: "nowrap",
//                           overflow: "hidden",
//                           textOverflow: "ellipsis",
//                         }}
//                       >
//                         {SelectedTaluka.length > 0
//                           ? SelectedTaluka.join(", ")
//                           : "Select Taluka"}
//                       </div>

//                       {openTaluka && (
//                         <div
//                           style={{
//                             position: "absolute",
//                             top: "44px",
//                             left: 0,
//                             width: "150px",
//                             background: "#fff",
//                             border: "1px solid #ccc",
//                             borderRadius: "8px",
//                             boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
//                             zIndex: 1000,
//                           }}
//                         >
//                           <label
//                             style={{
//                               display: "flex",
//                               alignItems: "center",
//                               padding: "8px",
//                               borderBottom: "1px solid #eee",
//                               cursor: "pointer",
//                               fontSize: "13px",
//                             }}
//                           >
//                             <input
//                               type="checkbox"
//                               checked={isAllTalukaSelected}
//                               onChange={toggleAllTalukas}
//                               style={{ marginRight: "6px" }}
//                             />
//                             {t("All Talukas")}
//                           </label>

//                           <div style={{ maxHeight: "150px", overflowY: "auto" }}>
//                             {allTalukas.map((taluka, index) => (
//                               <label
//                                 key={index}
//                                 style={{
//                                   display: "flex",
//                                   alignItems: "center",
//                                   padding: "7px 8px",
//                                   cursor: "pointer",
//                                   fontSize: "13px",
//                                 }}
//                               >
//                                 <input
//                                   type="checkbox"
//                                   checked={SelectedTaluka.includes(taluka)}
//                                   onChange={() => handleTalukaToggle(taluka)}
//                                   onClick={(e) => e.stopPropagation()}
//                                   style={{ marginRight: "6px" }}
//                                 />
//                                 {taluka}
//                               </label>
//                             ))}
//                           </div>

//                           <div
//                             style={{
//                               padding: "6px",
//                               textAlign: "right",
//                               borderTop: "1px solid #eee",
//                             }}
//                           >
//                             <button
//                               onClick={() => setOpenTaluka(false)}
//                               style={{
//                                 padding: "4px 10px",
//                                 fontSize: "12px",
//                                 border: "none",
//                                 borderRadius: "6px",
//                                 background: "#0d6efd",
//                                 color: "#fff",
//                                 cursor: "pointer",
//                               }}
//                             >
//                               OK
//                             </button>
//                           </div>
//                         </div>
//                       )}
//                     </div>
                

//                   {/* From + To */}
//                   <div
//                     style={{
//                       display: "flex",
//                       gap: "10px",
//                       alignItems: "center",
//                       flexShrink: 0,
//                     }}
//                   >
//                     <div className="input-group" style={{ width: "150px", height: "40px" }}>
//                       <span className="input-group-text">From</span>
//                       <input
//                         type="date"
//                         className="form-control"
//                         value={fromDate}
//                         onChange={(e) => setFromDate(e.target.value)}
//                         style={{
//                           fontSize: "14px",
//                           height: "40px",
//                         }}
//                       />
//                     </div>

//                     <div className="input-group" style={{ width: "150px", height: "40px" }}>
//                       <span className="input-group-text">To</span>
//                       <input
//                         type="date"
//                         className="form-control"
//                         value={toDate}
//                         onChange={(e) => setToDate(e.target.value)}
//                         style={{
//                           fontSize: "14px",
//                           height: "40px",
//                         }}
//                       />
//                     </div>
//                   </div>

//                   {/* Year */}
//                   <div style={{ width: "150px", flexShrink: 0 }}>
//                     <select
//                       value={approvedYear}
//                       onChange={(e) => setApprovedYear(e.target.value)}
//                       style={{
//                         width: "100%",
//                         height: "40px",
//                         padding: "6px 10px",
//                         border: "1px solid #ccc",
//                         borderRadius: "8px",
//                         fontSize: "14px",
//                         backgroundColor: "#f1f6fc",
//                       }}
//                     >
//                       <option value="">{t("Select Year")}</option>

//                       {[
//                         ...new Set(
//                           technicalInwards
//                             ?.map((item) => item.WorkApprovedYear)
//                             .filter(Boolean)
//                         ),
//                       ]
//                         .sort()
//                         .map((year, index) => (
//                           <option key={index} value={year}>
//                             {year}
//                           </option>
//                         ))}
//                     </select>
//                   </div>

//                   {/* Work Order Type */}
//                   <div style={{ width: "195px", flexShrink: 0 }}>
//                     <select
//                       value={selectedWorkOrderType}
//                       onChange={(e) => setSelectedWorkOrderType(e.target.value)}
//                       style={{
//                         width: "100%",
//                         height: "40px",
//                         padding: "6px 10px",
//                         border: "1px solid #ccc",
//                         borderRadius: "8px",
//                         fontSize: "14px",
//                         backgroundColor: "#f1f6fc",
//                         whiteSpace: "nowrap",
//                         overflow: "hidden",
//                         textOverflow: "ellipsis",
//                       }}
//                     >
//                       <option value="">Select Work Order Type</option>

//                       {[
//                         ...new Set(
//                           technicalInwards
//                             ?.map(
//                               (item) =>
//                                 item.WorkOrderType ||
//                                 item.workOrderType ||
//                                 item.Work_Order_Type
//                             )
//                             .filter(Boolean)
//                         ),
//                       ]
//                         .sort()
//                         .map((type, index) => (
//                           <option key={index} value={type}>
//                             {type}
//                           </option>
//                         ))}
//                     </select>
//                   </div>

                 


//                   {/* Search */}
//                   <div
//                     style={{
//                       position: "relative",
//                       width: "220px",
//                       flexShrink: 0,
//                     }}
//                   >
//                     <img
//                       src="/Search32.png"
//                       alt="Search"
//                       style={{
//                         position: "absolute",
//                         top: "50%",
//                         left: "10px",
//                         transform: "translateY(-50%)",
//                         width: "16px",
//                         opacity: 0.6,
//                       }}
//                     />

//                     <input
//                       type="text"
//                       placeholder={t("Search Here")}
//                       value={searchTerm}
//                       onChange={(e) => setSearchTerm(e.target.value)}
//                       style={{
//                         width: "100%",
//                         height: "40px",
//                         boxSizing: "border-box",
//                         padding: "8px 12px 8px 32px",
//                         border: "1px solid #ccc",
//                         borderRadius: "8px",
//                         backgroundColor: "#f1f6fc",
//                         fontSize: "14px",
//                       }}
//                     />
//                   </div>
//                 </div>
//                   {/* <div
//                     style={{
//                       display: "flex",
//                       flexWrap: "wrap",
//                       alignItems: "center",
//                       gap: "10px",
//                       width: "100%",
//                     }}
//                   >
//                     {UserRole === "State" && RoleID == 29 && (
//                       <div style={{ width: "150px", flexShrink: 0 }}>
//                         <select
//                           value={typeofData}
//                           onChange={(e) => settypeofData(e.target.value)}
//                           style={{
//                             width: "100%",
//                             height: "40px",
//                             padding: "6px 10px",
//                             border: "1px solid #ccc",
//                             borderRadius: "8px",
//                             fontSize: "14px",
//                             backgroundColor: "#f1f6fc",
//                             whiteSpace: "nowrap",
//                             overflow: "hidden",
//                             textOverflow: "ellipsis",
//                           }}
//                         >
//                           <option value="" disabled>
//                             Choose Data
//                           </option>

//                           <option value="District">
//                             {t(
//                               "3054-2419(Roads and bridge repair maintenance program)",
//                             )}
//                           </option>

//                           <option value="State">
//                             {t("2515-1238(MLA Representative)")}
//                           </option>
//                         </select>
//                       </div>
//                     )}

//                     <div style={{ width: "150px", flexShrink: 0 }}>
//                       <select
//                         value={statusofData1}
//                         onChange={(e) => setstatusofData1(e.target.value)}
//                         style={{
//                           width: "100%",
//                           height: "40px",
//                           padding: "6px 10px",
//                           border: "1px solid #ccc",
//                           borderRadius: "8px",
//                           fontSize: "14px",
//                           backgroundColor: "#f1f6fc",
//                         }}
//                       >
//                         <option value="">{t("All")}</option>
//                         <option value="Approved">{t("Approved")}</option>
//                         <option value="false">{t("Pending")}</option>
//                         <option value="Rejected">{t("Rejected")}</option>
//                         <option value="SendBack">{t("Sent Back")}</option>
//                       </select>
//                     </div>

//                     {UserRole === "State" && (
//                       <div
//                         style={{
//                           position: "relative",
//                           width: "150px",
//                           flexShrink: 0,
//                         }}
//                       >
//                         <div
//                           onClick={() => setOpenDistrict(!openDistrict)}
//                           style={{
//                             width: "100%",
//                             height: "40px",
//                             boxSizing: "border-box",
//                             padding: "9px 10px",
//                             border: "1px solid #ccc",
//                             borderRadius: "8px",
//                             backgroundColor: "#f1f6fc",
//                             cursor: "pointer",
//                             fontSize: "14px",
//                             whiteSpace: "nowrap",
//                             overflow: "hidden",
//                             textOverflow: "ellipsis",
//                           }}
//                         >
//                           {SelectedDistrict.length > 0
//                             ? SelectedDistrict.join(", ")
//                             : "Select District"}
//                         </div>

//                         {openDistrict && (
//                           <div
//                             style={{
//                               position: "absolute",
//                               top: "44px",
//                               left: 0,
//                               width: "150px",
//                               background: "#fff",
//                               border: "1px solid #ccc",
//                               borderRadius: "8px",
//                               boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
//                               zIndex: 1000,
//                             }}
//                           >
//                             <label
//                               style={{
//                                 display: "flex",
//                                 alignItems: "center",
//                                 padding: "8px",
//                                 borderBottom: "1px solid #eee",
//                                 cursor: "pointer",
//                                 fontSize: "13px",
//                               }}
//                             >
//                               <input
//                                 type="checkbox"
//                                 checked={isAllSelected}
//                                 onChange={toggleAllDistricts}
//                                 style={{ marginRight: "6px" }}
//                               />
//                               {t("All Districts")}
//                             </label>

//                             <div
//                               style={{ maxHeight: "150px", overflowY: "auto" }}
//                             >
//                               {[
//                                 ...new Set(
//                                   AllDistrict?.map((d) => d.District_Marathi),
//                                 ),
//                               ].map((district, index) => (
//                                 <label
//                                   key={index}
//                                   style={{
//                                     display: "flex",
//                                     alignItems: "center",
//                                     padding: "7px 8px",
//                                     cursor: "pointer",
//                                     fontSize: "13px",
//                                   }}
//                                 >
//                                   <input
//                                     type="checkbox"
//                                     checked={SelectedDistrict.includes(
//                                       district,
//                                     )}
//                                     onChange={() =>
//                                       handleDistrictToggle(district)
//                                     }
//                                     onClick={(e) => e.stopPropagation()}
//                                     style={{ marginRight: "6px" }}
//                                   />
//                                   {district}
//                                 </label>
//                               ))}
//                             </div>

//                             <div
//                               style={{
//                                 padding: "6px",
//                                 textAlign: "right",
//                                 borderTop: "1px solid #eee",
//                               }}
//                             >
//                               <button
//                                 onClick={() => setOpenDistrict(false)}
//                                 style={{
//                                   padding: "4px 10px",
//                                   fontSize: "12px",
//                                   border: "none",
//                                   borderRadius: "6px",
//                                   background: "#0d6efd",
//                                   color: "#fff",
//                                   cursor: "pointer",
//                                 }}
//                               >
//                                 OK
//                               </button>
//                             </div>
//                           </div>
//                         )}
//                       </div>
//                     )}

//                     {UserRole === "State" && (
//                       <div
//                         style={{
//                           position: "relative",
//                           width: "150px",
//                           flexShrink: 0,
//                         }}
//                       >
//                         <div
//                           onClick={() => setOpenTaluka(!openTaluka)}
//                           style={{
//                             width: "100%",
//                             height: "40px",
//                             boxSizing: "border-box",
//                             padding: "9px 10px",
//                             border: "1px solid #ccc",
//                             borderRadius: "8px",
//                             backgroundColor: "#f1f6fc",
//                             cursor: "pointer",
//                             fontSize: "14px",
//                             whiteSpace: "nowrap",
//                             overflow: "hidden",
//                             textOverflow: "ellipsis",
//                           }}
//                         >
//                           {SelectedTaluka.length > 0
//                             ? SelectedTaluka.join(", ")
//                             : "Select Taluka"}
//                         </div>

//                         {openTaluka && (
//                           <div
//                             style={{
//                               position: "absolute",
//                               top: "44px",
//                               left: 0,
//                               width: "150px",
//                               background: "#fff",
//                               border: "1px solid #ccc",
//                               borderRadius: "8px",
//                               boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
//                               zIndex: 1000,
//                             }}
//                           >
//                             <label
//                               style={{
//                                 display: "flex",
//                                 alignItems: "center",
//                                 padding: "8px",
//                                 borderBottom: "1px solid #eee",
//                                 cursor: "pointer",
//                                 fontSize: "13px",
//                               }}
//                             >
//                               <input
//                                 type="checkbox"
//                                 checked={isAllTalukaSelected}
//                                 onChange={toggleAllTalukas}
//                                 style={{ marginRight: "6px" }}
//                               />
//                               {t("All Talukas")}
//                             </label>

//                             <div
//                               style={{ maxHeight: "150px", overflowY: "auto" }}
//                             >
//                               {allTalukas.map((taluka, index) => (
//                                 <label
//                                   key={index}
//                                   style={{
//                                     display: "flex",
//                                     alignItems: "center",
//                                     padding: "7px 8px",
//                                     cursor: "pointer",
//                                     fontSize: "13px",
//                                   }}
//                                 >
//                                   <input
//                                     type="checkbox"
//                                     checked={SelectedTaluka.includes(taluka)}
//                                     onChange={() => handleTalukaToggle(taluka)}
//                                     onClick={(e) => e.stopPropagation()}
//                                     style={{ marginRight: "6px" }}
//                                   />
//                                   {taluka}
//                                 </label>
//                               ))}
//                             </div>

//                             <div
//                               style={{
//                                 padding: "6px",
//                                 textAlign: "right",
//                                 borderTop: "1px solid #eee",
//                               }}
//                             >
//                               <button
//                                 onClick={() => setOpenTaluka(false)}
//                                 style={{
//                                   padding: "4px 10px",
//                                   fontSize: "12px",
//                                   border: "none",
//                                   borderRadius: "6px",
//                                   background: "#0d6efd",
//                                   color: "#fff",
//                                   cursor: "pointer",
//                                 }}
//                               >
//                                 OK
//                               </button>
//                             </div>
//                           </div>
//                         )}
//                       </div>
//                     )}

//                     <div
//                       style={{
//                         display: "flex",
//                         gap: "10px",
//                         alignItems: "center",
//                         flexShrink: 0,
//                       }}
//                     >
//                       <div
//                         className="input-group"
//                         style={{ width: "150px", height: "40px" }}
//                       >
//                         <span className="input-group-text">From</span>
//                         <input
//                           type="date"
//                           className="form-control"
//                           value={fromDate}
//                           onChange={(e) => setFromDate(e.target.value)}
//                           style={{
//                             fontSize: "14px",
//                             height: "40px",
//                           }}
//                         />
//                       </div>

//                       <div
//                         className="input-group"
//                         style={{ width: "150px", height: "40px" }}
//                       >
//                         <span className="input-group-text">To</span>
//                         <input
//                           type="date"
//                           className="form-control"
//                           value={toDate}
//                           onChange={(e) => setToDate(e.target.value)}
//                           style={{
//                             fontSize: "14px",
//                             height: "40px",
//                           }}
//                         />
//                       </div>
//                     </div>

//                     <div style={{ width: "150px", flexShrink: 0 }}>
//                       <select
//                         value={approvedYear}
//                         onChange={(e) => setApprovedYear(e.target.value)}
//                         style={{
//                           width: "100%",
//                           height: "40px",
//                           padding: "6px 10px",
//                           border: "1px solid #ccc",
//                           borderRadius: "8px",
//                           fontSize: "14px",
//                           backgroundColor: "#f1f6fc",
//                         }}
//                       >
//                         <option value="">{t("Select Year")}</option>

//                         {[
//                           ...new Set(
//                             technicalInwards
//                               ?.map((item) => item.WorkApprovedYear)
//                               .filter(Boolean),
//                           ),
//                         ]
//                           .sort()
//                           .map((year, index) => (
//                             <option key={index} value={year}>
//                               {year}
//                             </option>
//                           ))}
//                       </select>
//                     </div>

//                     <div
//                       style={{
//                         position: "relative",
//                         width: "220px",
//                         flexShrink: 0,
//                       }}
//                     >
//                       <img
//                         src="/Search32.png"
//                         alt="Search"
//                         style={{
//                           position: "absolute",
//                           top: "50%",
//                           left: "10px",
//                           transform: "translateY(-50%)",
//                           width: "16px",
//                           opacity: 0.6,
//                         }}
//                       />

//                       <input
//                         type="text"
//                         placeholder={t("Search Here")}
//                         value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         style={{
//                           width: "100%",
//                           height: "40px",
//                           boxSizing: "border-box",
//                           padding: "8px 12px 8px 32px",
//                           border: "1px solid #ccc",
//                           borderRadius: "8px",
//                           backgroundColor: "#f1f6fc",
//                           fontSize: "14px",
//                         }}
//                       />
//                     </div>
//                   </div> */}
//                 </div>

//                 <div className="col-12 pb-5">
//                   <div
//                     className="m-auto mb-3"
//                     style={{
//                       backgroundColor: "white",
//                       padding: "40px 40px",
//                       boxShadow: "0px 0px 5px 1px #E4E8EC",
//                       borderRadius: "20px",
//                       width: "100%",
//                       maxWidth: "100%",
//                       overflowX: "hidden",
//                     }}
//                   >
//                     <div
//                       className="col-12 tableDesign"
//                       style={{ background: "#2C78DB",  overflowX: "auto",
//                       overflowY: "auto",
//                       maxHeight: "600px", // change height as needed
//                       position: "relative"}}
//                     >
//                       <table
//                         className="m-0 p-0 BillsOutwardTable"
//                         style={{ width: "100%", borderCollapse: "collapse" }}
//                       >
//                         <thead style={{ position: "sticky",  backgroundColor: "#1976d2",
//                       top: 0,
//                       zIndex: 10,}}>
//                           <tr
//                             style={{
//                               color: "white",
//                               border: "none",
//                               height: "3rem",
//                               fontSize: "18px",
//                             }}
//                           >
//                             <th
//                               style={{ minWidth: "70px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Sr.No.")}
//                             </th>
//                             <th
//                               style={{ minWidth: "130px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Demand ID")}
//                             </th>
//                             <th
//                               style={{ minWidth: "130px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("RA Bill Number")}
//                             </th>
//                             <th
//                               style={{ minWidth: "130px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Class Of Work")}
//                             </th>
//                             <th
//                               style={{ minWidth: "130px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Type Of Work")}
//                             </th>
//                             <th
//                             style={{ minWidth: "160px", padding: "8px" }}
//                             rowSpan={2}
//                             colSpan={1}
//                           >
//                             {t("Work Order Type")}
//                           </th>
//                             <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Work Name")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("District")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Taluka")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Work Apprved Year")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Administrative Approved GR Date")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Technical Sanction Number")}
//                             </th>
//                             {UserRole === "State" && RoleID != 29 ? (
//                               ""
//                             ) : (
//                               <th
//                                 style={{ minWidth: "250px", padding: "8px" }}
//                                 rowSpan={2}
//                                 colSpan={1}
//                               >
//                                 {t("Head ID")}
//                               </th>
//                             )}
//                             <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Job ID")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Physical Progress(in %)")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Vendor Name")}
//                             </th>
//                             {/* <th style={{ minWidth: "150px", padding: "8px" }} rowSpan={2} colSpan={1}>{t("Work Order UID")}</th> */}
//                             <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Bill Copy")}
//                             </th>
//                             {/* <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Demand Copy")}
//                             </th> */}
//                             <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Measurement Book")}
//                             </th>
//                             <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("SQM Inspection Book")}
//                             </th>
//                             <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Work Completion Certificate")}
//                             </th>
//                             <th
//                               style={{ minWidth: "250px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Administrative Approved GR Copy")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Remark")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Reject/SentBack Remark")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Bill Amount")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Net Payable Amount")}
//                             </th>
//                             <th
//                               style={{ minWidth: "150px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Deduction Amount")}
//                             </th>
//                             <th
//                               style={{ minWidth: "190px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Creation Date")}
//                             </th>
//                             {(RoleID === 14 || RoleID === 31 ||RoleID === 80 ) && (
//                               <th
//                                 style={{ minWidth: "120px", padding: "8px" }}
//                                 rowSpan={2}
//                                 colSpan={1}
//                               >
//                                 {t("Action")}
//                               </th>
//                             )}
//                             <th
//                               style={{ minWidth: "120px", padding: "8px" }}
//                               rowSpan={2}
//                               colSpan={1}
//                             >
//                               {t("Action By")}
//                             </th>
//                             <th style={{ padding: "8px" }} colSpan={8}>
//                               {t("Approved by")}
//                             </th>{" "}
//                             {/* changed from 9 to 7 */}
//                             {/* <th style={headerStyle} colSpan={1} rowSpan={2}>Details</th> */}
//                           </tr>
//                           <tr
//                             style={{
//                               color: "white",
//                               border: "none",
//                               height: "3rem",
//                             }}
//                           >
//                             {/* 1st Column */}
//                             <th style={{ minWidth: "150px", padding: "8px" }}>
//                               {isChecker
//                                 ? t("Checker")
//                                 : t("Department Auditor")}
//                             </th>

//                             {/* 2nd Column - Hide for RoleID 80, 81 */}
//                             {!isChecker && (
//                               <th style={{ minWidth: "150px", padding: "8px" }}>
//                                 {t("Department HOD(EE)")}
//                               </th>
//                             )}

//                             {/* 3rd Column - Always Show */}
//                             <th style={{ minWidth: "150px", padding: "8px" }}>
//                               {t("FD Auditor")}
//                             </th>

//                             {/* 4th Column - Hide for RoleID 80, 81 */}
//                             {!isChecker && (
//                               <th style={{ minWidth: "150px", padding: "8px" }}>
//                                 {t("FD AAO")}
//                               </th>
//                             )}

//                             {/* <th style={{ minWidth: "150px", padding: "8px" }}>
//                     {t("FD AO")}
//                   </th> */}

//                             {/* 5th Column - Hide for RoleID 80, 81 */}
//                             {!isChecker && (
//                               <th style={{ minWidth: "150px", padding: "8px" }}>
//                                 {t("Deputy CAFO")}
//                               </th>
//                             )}

//                             {/* 6th Column - Always Show */}
//                             <th style={{ minWidth: "150px", padding: "8px" }}>
//                               {t("CAFO")}
//                             </th>

//                             {/* 7th Column - Always Show */}
//                             <th style={{ minWidth: "150px", padding: "8px" }}>
//                               {t("RDD")}
//                             </th>

//                             {/* <th style={{ minWidth: "150px", padding: "8px" }}>
//                             {t("RDD Section Officer ")}
//                           </th> */}
//                           </tr>
//                         </thead>

//                         <tbody>
//                           <>
//                             {displayedGRS?.length === 0 ? (
//                               <tr>
//                                 <td
//                                   colSpan="30"
//                                   style={{
//                                     textAlign: "center",
//                                     padding: "20px",
//                                     backgroundColor: "#f9f9f9",
//                                   }}
//                                 >
//                                   {t("No data available")}
//                                 </td>
//                               </tr>
//                             ) : (
//                               displayedGRS?.map((proposal, index) => {
//                                 const bgColor =
//                                   index % 2 === 0 ? "#E5F4FB" : "#CFE4F1";
//                                 return (
//                                   <tr
//                                     style={{
//                                       backgroundColor: bgColor,
//                                       fontSize: "18px",
//                                     }}
//                                     key={proposal.DemandGenerationID || index}
//                                   >
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {indexOfFirstBill + index + 1}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       <span
//                                         onClick={() =>
//                                           settingshowspecific3(
//                                             proposal.DemandGenerationID,
//                                           )
//                                         }
//                                         style={{
//                                           color: "#0d6efd",
//                                           textDecoration: "underline",
//                                           cursor: "pointer",
//                                           fontWeight: "500",
//                                         }}
//                                       >
//                                         {proposal.DemandGenerationID}
//                                       </span>
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.RABillNumber}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.classOfWork ||
//                                         proposal.workClass}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.typeOfWork &&
//                                       proposal.typeOfWork !== "NULL"
//                                         ? proposal.typeOfWork
//                                         : proposal.workType &&
//                                             proposal.workType !== "NULL"
//                                           ? proposal.workType
//                                           : "-"}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                     {proposal.WorkOrderType ||
//                                       proposal.workOrderType ||
//                                       "-"}
//                                   </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.ProjectID}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.District}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.ulb || proposal.Taluka || "-"}
//                                     </td>
//                                     <td
//                                       style={{
//                                         width: "140px",
//                                         minWidth: "140px",
//                                         padding: "10px",
//                                         textAlign: "center",
//                                       }}
//                                     >
//                                       {proposal.WorkApprovedYear || "-"}
//                                     </td>
//                                     {/* <td>
//                                       {(proposal.GRApproveDate &&
//                                         new Date(
//                                           proposal.GRApproveDate,
//                                         ).toLocaleString()) ||
//                                         "-"}
//                                     </td> */}
//                                     <td>
//                                       {proposal.GRApproveDate ||
//                                       proposal.workApprovedGRDate
//                                         ? formatDate(
//                                             proposal.GRApproveDate ||
//                                               proposal.workApprovedGRDate,
//                                           )
//                                         : "-"}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.BillNumber || "-"}
//                                     </td>
//                                     {UserRole === "State" && RoleID != 29 ? (
//                                       ""
//                                     ) : (
//                                       <td style={{ padding: "8px 8px" }}>
//                                         {proposal.BudgetID}
//                                       </td>
//                                     )}
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.JOB_ID || "-"}
//                                     </td>
//                                     <td>
//                                       <span
//                                         style={{
//                                           color: "blue",
//                                           textDecoration: "underline",
//                                           cursor: "pointer",
//                                         }}
//                                         onClick={() =>
//                                           openProgressModal(
//                                             proposal.Upload,
//                                             proposal.Location,
//                                           )
//                                         }
//                                       >
//                                         {proposal.physicalValueOfProgress}%
//                                       </span>
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.ContractorName}
//                                     </td>
//                                     {/* <td style={{ padding: '8px 8px' }}>{proposal.WorkOrderUID}</td> */}
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.UploadBillCopy ? (
//                                         <a
//                                           href={`${apiurl}/BillCopyDocsImages/${proposal.UploadBillCopy}`}
//                                           target="_blank"
//                                         >
//                                           {formatFileName(
//                                             proposal.UploadBillCopy,
//                                           )}
//                                         </a>
//                                       ) : (
//                                         <p className="fw-bold text-danger my-auto">
//                                           {t("No File")}
//                                         </p>
//                                       )}
//                                     </td>
//                                     {/* <td
//                                       style={{
//                                         fontSize: "12px",
//                                         padding: "5px",
//                                       }}
//                                     >
//                                       <button
//                                         className="btn py-1 d-flex align-items-center justify-content-center gap-2 mx-auto"
//                                         style={{
//                                           minHeight: "33px",

//                                           minWidth: "100px",

//                                           padding: "0.375rem 0.75rem",

//                                           fontSize: "0.9rem",

//                                           fontWeight: 400,

//                                           lineHeight: 1.5,

//                                           cursor: "pointer",

//                                           background:
//                                             "linear-gradient(to right, #38439f, #048ecc)",

//                                           color: "white",

//                                           borderRadius: "999px",

//                                           border: "1px solid transparent",
//                                         }}
//                                         onClick={() =>
//                                           settingshowspecific3(
//                                             proposal.DemandGenerationID,
//                                           )
//                                         }
//                                       >
//                                         <img
//                                           src="/view32.png"
//                                           alt="view"
//                                           width="15"
//                                           height="15"
//                                           style={{ verticalAlign: "middle" }}
//                                         />

//                                         <span style={{ lineHeight: 1 }}>
//                                           View{" "}
//                                         </span>
//                                       </button>
//                                     </td> */}
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Measurement_Book ? (
//                                         <a
//                                           href={`${apiurl}/Measurement_BookImages/${proposal.Measurement_Book}`}
//                                           target="_blank"
//                                         >
//                                           {formatFileName(
//                                             proposal.Measurement_Book,
//                                           )}
//                                         </a>
//                                       ) : (
//                                         <p className="fw-bold text-danger my-auto">
//                                           {t("No File")}
//                                         </p>
//                                       )}
//                                     </td>{" "}
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Inspection_Report ? (
//                                         <a
//                                           href={`${apiurl}/Inspection_ReportImages/${proposal.Inspection_Report}`}
//                                           target="_blank"
//                                         >
//                                           {formatFileName(
//                                             proposal.Inspection_Report,
//                                           )}
//                                         </a>
//                                       ) : (
//                                         <p className="fw-bold text-danger my-auto">
//                                           {t("No File")}
//                                         </p>
//                                       )}
//                                     </td>{" "}
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Work_completion ? (
//                                         <a
//                                           href={`${apiurl}/Work_completionImages/${proposal.Work_completion}`}
//                                           target="_blank"
//                                         >
//                                           {formatFileName(
//                                             proposal.Work_completion,
//                                           )}
//                                         </a>
//                                       ) : (
//                                         <p className="fw-bold text-danger my-auto">
//                                           {t("No File")}
//                                         </p>
//                                       )}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.GRCopy ? (
//                                         <a
//                                           href={`${apiurl}/UploadedGR/${proposal.GRCopy}`}
//                                           target="_blank"
//                                         >
//                                           {formatFileName(proposal.GRCopy)}
//                                         </a>
//                                       ) : (
//                                         <p className="fw-bold text-danger my-auto">
//                                           {t("No File")}
//                                         </p>
//                                       )}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Remark}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.ActionDemandRemark === "NULL"
//                                         ? "-"
//                                         : proposal.ActionDemandRemark || "-"}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.BillAmount}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.NetPayableAmount}
//                                     </td>
//                                     {/* <td style={{ padding: "8px 8px" }}>
//                                       {proposal.DeductionAmount}
//                                     </td> */}
//                                     <td
//                                     onClick={() =>
//                                       DemandDeductionDetailByBillID(
//                                         proposal.DemandGenerationID,
//                                       )
//                                     }
//                                   >
//                                     <p
//                                       className="makeitlink my-auto"
//                                       style={{ textAlign: "center" }}
//                                     >
//                                       {Number(proposal.DeductionAmount).toLocaleString("en-IN")}
//                                     </p>
//                                   </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {formatDateTime(proposal.Date)}
//                                     </td>
//                                     {(RoleID === 14 || RoleID === 31||RoleID === 80) && (
//                                       <td style={{ padding: "8px 8px" }}>
//                                         {proposal.Status === "SendBack" &&
//                                         proposal.UserID == currentUserID ? (
//                                           <button
//                                             onClick={() =>
//                                               navigate(
//                                                 `/AdminDashboard/EditDemandGeneration/${proposal.DemandGenerationID}`,
//                                               )
//                                             }
//                                             style={{
//                                               background:
//                                                 "linear-gradient(to right, #f0ad4e, #ec971f)",
//                                               color: "white",
//                                               border: "none",
//                                               padding: "6px 14px",
//                                               borderRadius: "16px",
//                                               fontSize: "12px",
//                                               fontWeight: "600",
//                                               cursor: "pointer",
//                                             }}
//                                           >
//                                             {t("Edit")}
//                                           </button>
//                                         ) : (
//                                           "-"
//                                         )}
//                                       </td>
//                                     )}
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Status === "SendBack"
//                                         ? `Send Back By - ${proposal.ActionBy === "NULL" ? "-" : proposal.ActionBy}`
//                                         : proposal.Status === "Rejected"
//                                           ? `Rejected By - ${proposal.ActionBy === "NULL" ? "-" : proposal.ActionBy}`
//                                           : proposal.ActionBy === "NULL"
//                                             ? "-"
//                                             : proposal.ActionBy || "-"}
//                                     </td>
//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Approvance1 === "true" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#16C60C", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✓
//                                         </div>
//                                       ) : proposal.Approvance1 === "false" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#dc2626", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✕
//                                         </div>
//                                       ) : (
//                                         "-"
//                                       )}
//                                     </td>

//                                     {!isChecker && (
//                                       <td style={{ padding: "8px 8px" }}>
//                                         {proposal.Approvance2 === "true" ? (
//                                           <div className="m-auto" style={{
//                                             width: "20px", height: "20px", borderRadius: "6px",
//                                             backgroundColor: "#16C60C", border: "none",
//                                             display: "flex", justifyContent: "center", alignItems: "center",
//                                             fontSize: "13px", color: "white",
//                                             boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                           }}>
//                                             ✓
//                                           </div>
//                                         ) : proposal.Approvance2 === "false" ? (
//                                           <div className="m-auto" style={{
//                                             width: "20px", height: "20px", borderRadius: "6px",
//                                             backgroundColor: "#dc2626", border: "none",
//                                             display: "flex", justifyContent: "center", alignItems: "center",
//                                             fontSize: "13px", color: "white",
//                                             boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                           }}>
//                                             ✕
//                                           </div>
//                                         ) : (
//                                           "-"
//                                         )}
//                                       </td>
//                                     )}

//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Approvance3 === "true" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#16C60C", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✓
//                                         </div>
//                                       ) : proposal.Approvance3 === "false" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#dc2626", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✕
//                                         </div>
//                                       ) : (
//                                         "-"
//                                       )}
//                                     </td>

//                                     {!isChecker && (
//                                       <td style={{ padding: "8px 8px" }}>
//                                         {proposal.Approvance4 === "true" ? (
//                                           <div className="m-auto" style={{
//                                             width: "20px", height: "20px", borderRadius: "6px",
//                                             backgroundColor: "#16C60C", border: "none",
//                                             display: "flex", justifyContent: "center", alignItems: "center",
//                                             fontSize: "13px", color: "white",
//                                             boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                           }}>
//                                             ✓
//                                           </div>
//                                         ) : proposal.Approvance4 === "false" ? (
//                                           <div className="m-auto" style={{
//                                             width: "20px", height: "20px", borderRadius: "6px",
//                                             backgroundColor: "#dc2626", border: "none",
//                                             display: "flex", justifyContent: "center", alignItems: "center",
//                                             fontSize: "13px", color: "white",
//                                             boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                           }}>
//                                             ✕
//                                           </div>
//                                         ) : (
//                                           "-"
//                                         )}
//                                       </td>
//                                     )}

//                                   {/* <td style={{ padding: "8px 8px" }}>
//   {proposal.Approvance5 === "true" ? (
//     ...
//   ) : proposal.Approvance5 === "false" ? (
//     ...
//   ) : (
//     "-"
//   )}
// </td> */}

//                                     {!isChecker && (
//                                       <td style={{ padding: "8px 8px" }}>
//                                         {proposal.Approvance6 === "true" ? (
//                                           <div className="m-auto" style={{
//                                             width: "20px", height: "20px", borderRadius: "6px",
//                                             backgroundColor: "#16C60C", border: "none",
//                                             display: "flex", justifyContent: "center", alignItems: "center",
//                                             fontSize: "13px", color: "white",
//                                             boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                           }}>
//                                             ✓
//                                           </div>
//                                         ) : proposal.Approvance6 === "false" ? (
//                                           <div className="m-auto" style={{
//                                             width: "20px", height: "20px", borderRadius: "6px",
//                                             backgroundColor: "#dc2626", border: "none",
//                                             display: "flex", justifyContent: "center", alignItems: "center",
//                                             fontSize: "13px", color: "white",
//                                             boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                           }}>
//                                             ✕
//                                           </div>
//                                         ) : (
//                                           "-"
//                                         )}
//                                       </td>
//                                     )}

//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Approvance7 === "true" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#16C60C", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✓
//                                         </div>
//                                       ) : proposal.Approvance7 === "false" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#dc2626", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✕
//                                         </div>
//                                       ) : (
//                                         "-"
//                                       )}
//                                     </td>

//                                   {/* <td style={{ padding: '8px 8px' }}>
//   {proposal.Approvance8 === 'true' ? (...) : proposal.Approvance8 === 'false' ? (...) : '-'}
// </td> */}

//                                     <td style={{ padding: "8px 8px" }}>
//                                       {proposal.Approvance8 === "true" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#16C60C", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✓
//                                         </div>
//                                       ) : proposal.Approvance8 === "false" ? (
//                                         <div className="m-auto" style={{
//                                           width: "20px", height: "20px", borderRadius: "6px",
//                                           backgroundColor: "#dc2626", border: "none",
//                                           display: "flex", justifyContent: "center", alignItems: "center",
//                                           fontSize: "13px", color: "white",
//                                           boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
//                                         }}>
//                                           ✕
//                                         </div>
//                                       ) : (
//                                         "-"
//                                       )}
//                                     </td>
//                                     {/* <td style={{ padding: '8px' }}>{proposal.Proposal_copy}</td> */}
//                                     {/* <td>
//                                                             <button
//                                                                 className='m-auto'
//                                                                 onClick={() => selectedBillDetails(proposal.DemandGenerationID)}
//                                                                 style={{
//                                                                     cursor: 'pointer',
//                                                                     background: "linear-gradient(to right, #3b409e, #0067ab, #0f79ba, #0098d5)",
//                                                                     color: 'white',
//                                                                     border: 'none',
//                                                                     padding: '6px 20px',
//                                                                     borderRadius: '4px',
//                                                                     fontSize: "12px",
//                                                                     display: "flex",
//                                                                     justifyContent: "center",
//                                                                     alignItems: 'center',
//                                                                     width: "80%"
//                                                                 }}
//                                                             >
//                                                                 <img src="/view32.png" alt="view" className='me-1' style={{ width: "12px", height: "12px" }} />
//                                                                 View
//                                                             </button>
//                                                         </td> */}
//                                   </tr>
//                                 );
//                               })
//                             )}
//                             <tr
//                               style={{
//                                 fontWeight: "600",
//                                 backgroundColor: "#f0f0f0",
//                                 fontSize: "18px",
//                               }}
//                             >
//                               <td
//                                 colSpan={
//                                   UserRole == "State" && RoleID != 34 ? 22 : 23
//                                 }
//                                 style={{ textAlign: "right", padding: "8px" }}
//                               >
//                                 {t("Page Total")}
//                               </td>

//                               <td style={{ padding: "8px" }}>
//                                 ₹ {pagetotalBillamt.toLocaleString("en-IN")}
//                               </td>
//                               <td style={{ padding: "8px" }}>
//                                 ₹ {pagetotalpayableamt.toLocaleString("en-IN")}
//                               </td>
//                               <td style={{ padding: "8px" }}>
//                                 ₹{" "}
//                                 {pagetotaldeductionamt.toLocaleString("en-IN")}
//                               </td>
//                               {/* remaining columns */}
//                               <td colSpan={UserRole == "State" ? 22 : 23}></td>
//                             </tr>
//                             <tr
//                               style={{
//                                 fontWeight: "600",
//                                 backgroundColor: "#f0f0f0",
//                                 fontSize: "18px",
//                               }}
//                             >
//                               <td
//                                 colSpan={
//                                   UserRole == "State" && RoleID != 34 ? 22 : 23
//                                 }
//                                 style={{ textAlign: "right", padding: "8px" }}
//                               >
//                                 {t("Gross Total")}
//                               </td>

//                               <td style={{ padding: "8px" }}>
//                                 ₹ {totalBillamt.toLocaleString("en-IN")}
//                               </td>
//                               <td style={{ padding: "8px" }}>
//                                 ₹ {totalpayableamt.toLocaleString("en-IN")}
//                               </td>
//                               <td style={{ padding: "8px" }}>
//                                 ₹ {totaldeductionamt.toLocaleString("en-IN")}
//                               </td>
//                               {/* remaining columns */}
//                               <td colSpan={UserRole == "State" ? 22 : 23}></td>
//                             </tr>
//                           </>
//                         </tbody>
//                       </table>
//                     </div>
//                     <div
//                       style={{
//                         display: "flex",
//                         justifyContent: "space-between",
//                         height: "4rem",
//                         alignItems: "center",
//                       }}
//                       className="mt-3"
//                     >
//                       <div>
//                         <p
//                           className="text-gray-600"
//                           style={{ fontSize: "18px" }}
//                         >
//                           {proposallist.length > 0
//                             ? `Showing ${indexOfFirstBill + 1} to ${Math.min(indexOfLastBill, filteredBills.length)} of ${totalBillsCount} entries`
//                             : "Showing 0 to 0 of 0 entries"}
//                         </p>
//                       </div>
//                       <div
//                         style={{
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "10px",
//                         }}
//                       >
//                         <GooglePagination
//                           currentPage={currentPage}
//                           totalPages={totalPages}
//                           onPageChange={setCurrentPage}
//                         />

//                         <button
//                           onClick={() => {
//                             setShowAll(!showAll);
//                             setCurrentPage(1);
//                           }}
//                           style={{
//                             border: "none",
//                             outline: "none",
//                             cursor: "pointer",
//                             padding: "8px 16px",
//                             borderRadius: "20px",
//                             fontSize: "18px",
//                             fontWeight: "600",
//                             color: "#fff",
//                             background: showAll
//                               ? "linear-gradient(135deg, #28a745, #20c997)"
//                               : "linear-gradient(135deg, #0067ab, #0098d5)",
//                             boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
//                             transition: "all 0.3s ease",
//                           }}
//                         >
//                           {showAll ? "✓ Showing All" : "View All"}
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {selectedProposal && (
//               <div
//                 onClick={() => setSelectedProposal(null)}
//                 style={{
//                   position: "fixed",
//                   top: 0,
//                   left: 0,
//                   width: "100vw",
//                   height: "100vh",
//                   backgroundColor: "rgba(0, 0, 0, 0.84)",
//                   display: "flex",
//                   justifyContent: "center",
//                   alignItems: "center",
//                   zIndex: 9999,
//                 }}
//               >
//                 <div
//                   onClick={(e) => e.stopPropagation()}
//                   style={{
//                     backgroundColor: "white",
//                     padding: "40px 40px",
//                     borderRadius: "20px",
//                     width: "50%",
//                     maxHeight: "90vh",
//                     overflow: "hidden",
//                     boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
//                     position: "relative",
//                   }}
//                 >
//                   <div className="col-12">
//                     <div className="col-12  mb-3">
//                       <div className="row">
//                         <div className="col-6">
//                           <h5 className="fw-bold">{t("Bill Details")}</h5>
//                         </div>

//                         <div
//                           className="col-6"
//                           style={{
//                             display: "flex",
//                             justifyContent: "flex-end",
//                           }}
//                         >
//                           <button
//                             onClick={() => setSelectedProposal(null)}
//                             style={{
//                               width: "32px",
//                               height: "32px",
//                               borderRadius: "50%",
//                               backgroundColor: "red",
//                               border: "none",
//                               display: "flex",
//                               justifyContent: "center",
//                               alignItems: "center",
//                               cursor: "pointer",
//                               fontSize: "24px",
//                               fontWeight: "bold",
//                               color: "white",
//                               lineHeight: 1,
//                             }}
//                           >
//                             ×
//                           </button>
//                         </div>
//                       </div>
//                     </div>

//                     <div
//                       className="col-12"
//                       style={{
//                         height: "59vh",
//                         overflowX: "hidden",
//                         overflowY: "scroll",
//                         scrollBehavior: "smooth",
//                       }}
//                     >
//                       <table
//                         style={{
//                           width: "100%",
//                           height: "100%",
//                           borderCollapse: "collapse",
//                         }}
//                       >
//                         <tbody>
//                           {Object.entries(selectedProposal).map(
//                             ([key, value], index) => (
//                               <>
//                                 <tr
//                                   key={key}
//                                   style={{
//                                     backgroundColor:
//                                       index % 2 === 0 ? "#2B75D5" : "#cce0f9",
//                                     border: "1px solid #cce0f9",
//                                   }}
//                                 >
//                                   <td
//                                     style={{
//                                       fontWeight: "bold",
//                                       padding: "8px",
//                                       width: "40%",
//                                       backgroundColor: "#2B75D5",
//                                       color: "white",
//                                       border: "1px solid #cce0f9",
//                                     }}
//                                   >
//                                     {key}
//                                   </td>
//                                   <td
//                                     style={{
//                                       padding: "8px",
//                                       color: "black",
//                                       backgroundColor:
//                                         index % 2 === 0 ? "#E3EBF5" : "#D0DEF1",
//                                     }}
//                                   >
//                                     {/* Handle Upload Bill Copy link */}
//                                     {key === "Upload Demand Copy" && value ? (
//                                       <a
//                                         href={`${apiurl}/BillCopyDocsImages/${value}`}
//                                         target="_blank"
//                                         rel="noreferrer"
//                                       >
//                                         {value}
//                                       </a>
//                                     ) : value === null || value === "" ? (
//                                       "-"
//                                     ) : (
//                                       value.toString()
//                                     )}
//                                   </td>
//                                 </tr>
//                               </>
//                             ),
//                           )}
//                         </tbody>
//                       </table>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>
//         )}

//         {showspecific === 4 && (
//           <>
//             <div className="col-12 m-auto">
//               <div
//                 className="col-12 m-auto my-15 mb-10 budgetContainer "
//                 style={{
//                   maxHeight: "85vh", // or 700px

//                   overflowY: "auto",

//                   overflowX: "hidden",

//                   border: "1px solid #ddd",

//                   borderRadius: "8px",
//                 }}
//               >
//                 <div className="m-auto budgetElement">
//                   {/* Heading */}

//                   {/* TABLE CONTAINER */}

//                   <div
//                     className="mt-3 d-flex justify-content-between"
//                     style={{
//                       position: "relative",

//                       alignItems: "center",

//                       background: "#fff",
//                     }}
//                   >
//                     {/* TITLE – LEFT */}

//                     <div className="d-flex align-items-center gap-2 mb-3">
//                       <img
//                         src="/Bill-files64.png"
//                         alt="MAS Slip"
//                         width="26"
//                         height="26"

//                         // style={{ transform: "translateY(9px)", }}
//                       />

//                       <h5 className="m-0 fw-bold">Bill Slip</h5>
//                     </div>

//                     {/* BACK BUTTON – TOP RIGHT */}

//                     <div className="col-4 pb-3 d-flex justify-content-end gap-2">
//                       <button
//                         className="btn btn-primary d-flex align-items-center gap-2"
//                         style={{
//                           borderRadius: "999px",

//                           padding: "6px 22px",

//                           fontSize: "14px",

//                           zIndex: 10,
//                         }}
//                         onClick={exportBillSlipPDF}
//                         disabled={exportingBillSlipPDF}
//                       >
//                         {exportingBillSlipPDF ? "Exporting..." : "Export PDF"}
//                       </button>

//                       <button
//                         className="btn btn-danger d-flex align-items-center gap-2"
//                         style={{
//                           top: "12px",

//                           right: "12px",

//                           borderRadius: "999px",

//                           padding: "6px 22px",

//                           fontSize: "14px",

//                           zIndex: 10,
//                         }}
//                         onClick={BackFunction}
//                       >
//                         {/* <img

//                         src={Back32}

//                         alt="back"

//                         width="14"

//                         height="14"

//                         // style={{ filter: "brightness(0) invert(1)" }}

//                       /> */}
//                         Back
//                       </button>
//                     </div>
//                   </div>

//                   <div ref={billSlipRef}>
//                     <div
//                       className="text-center text-white py-3"
//                       style={{
//                         background: "linear-gradient(90deg, #2b4ea1, #0aa2d6)",
//                       }}
//                     >
//                       <div style={{ fontSize: "18px", fontWeight: "bold" }}>
//                         Zila Parishad - {selectedBillData.District}
//                       </div>

//                       {/* <div style={{ fontSize: "15px", fontWeight: "bold" }}>
//                         प्रशासकीय मंजुरी वर्ष - 2026-2027
//                       </div>

//                       <div>
//                         विभाग - EXE.ENGINEER ZP Division{" "}
//                         {selectedBillData.Division}
//                       </div>

//                       <div>उप-विभाग - {selectedBillData.District}</div>

//                       <div class="small">
//                         या तपशीलवार शीर्षाचे देयकासाठी आवश्यक असेल तिथे वित्त
//                         विभागाचा अनौपचारिक संदर्भ तपासावा.
//                       </div> */}
//                     </div>

//                     <div className="border border-dark p-1">
//                       <table
//                         className="table mb-0"
//                         style={{ fontSize: "18px", border: "none" }}
//                       >
//                         <tbody>
//                           {/* <tr>

//                           <td className='text-start'>

//                             <span>UserName :</span>



//                           </td>

//                           <td className='text-end'>

//                             <span>DateTime : </span>

//                           </td>

//                         </tr> */}

//                           <tr>
//                             <td
//                               colSpan={2}
//                               className="fw-bold text-center fs-6 p-1"
//                             >
//                               {" "}
//                               Bill Slip for RDD
//                             </td>
//                           </tr>

//                           {[
//                             [
//                               "User Name",
//                               `${selectedBillData.UploadedBy ?? "-"}`,
//                             ],
//                             [
//                               "Demand ID",
//                               `${selectedBillData.DemandGenerationID ?? "-"}`,
//                             ],
//                             [
//                               "Bill Creation Date",
//                               formatDateTime(selectedBillData.UploadedOn),
//                             ],
//                             [
//                               "Head Code",
//                               `${selectedBillData.BudgetID ?? "-"}`,
//                             ],
//                             [
//                               "Work Name",
//                               `${selectedBillData.ProjectID ?? "-"}`,
//                             ],
//                             [
//                               "Class of Work",
//                               `${selectedBillData.classOfWork || selectedBillData.workClass || "-"}`,
//                             ],
//                             [
//                               "Work Approved Year",
//                               `${selectedBillData.ProposalYear ?? "-"}`,
//                             ],
//                             [
//                               "Work order date",
//                               formatDate(selectedBillData.WorkOrderDate),
//                               ,
//                             ],
//                             [
//                               "Work Approved Amount",
//                               `${selectedBillData.ApprovedWorkAmount ?? "-"}`,
//                             ],
//                             [
//                               "Gross Bill Amount",
//                               `${selectedBillData.BillAmount ?? "-"}`,
//                             ],
//                             [
//                               "R.A.Bill No:",
//                               `${selectedBillData.RABillNumber ?? "-"}`,
//                             ],

//                             [
//                               "Invoice Bill No",
//                               `${selectedBillData.InvoiceNo ?? "-"}`,
//                             ],
//                           ].map(([label, value], index) => (
//                             <tr
//                               key={index}
//                               style={{ border: "1.5px solid #1190CA" }}
//                             >
//                               <td
//                                 className="text-start"
//                                 style={{
//                                   width: "30%",

//                                   backgroundColor: "#EAF3FB",

//                                   fontWeight: 500,

//                                   padding: "6px 10px",

//                                   borderRight: "1.5px solid #1190CA",
//                                 }}
//                               >
//                                 {label}
//                               </td>

//                               <td
//                                 className="text-start"
//                                 style={{
//                                   width: "70%",

//                                   padding: "6px 10px",

//                                   backgroundColor: "#FFFFFF",
//                                 }}
//                               >
//                                 {value}
//                               </td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </table>

//                       <table
//                         className="mt-1"
//                         style={{
//                           borderCollapse: "collapse",

//                           border: "1px solid #000",
//                         }}
//                       >
//                         <tbody>
//                           <tr className="border border-dark">
//                             <td
//                               className="text-start border border-dark "
//                               style={{ width: "30%" }}
//                             >
//                               Bill Amount
//                             </td>

//                             <td
//                               className="text-start border border-dark"
//                               style={{ width: "23%" }}
//                             >
//                               Deduction Amount
//                             </td>

//                             <td
//                               className="text-start border border-dark "
//                               style={{ width: "24%" }}
//                             >
//                               Net Payable
//                             </td>
//                           </tr>

//                           <tr>
//                             <td className="text-start border border-dark">
//                               {selectedBillData.BillAmount}
//                             </td>

//                             <td className="text-start border border-dark">
//                               {selectedBillData.DeductionAmount}
//                             </td>

//                             <td className="text-start border border-dark">
//                               {selectedBillData.NetPayableAmount}
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>

//                       {/* Vendor Details */}

//                       <table
//                         className="mt-2"
//                         style={{
//                           width: "100%",

//                           borderCollapse: "collapse",

//                           fontSize: "18px",
//                         }}
//                       >
//                         <tbody>
//                           {/* Section Title */}

//                           <tr
//                             style={{
//                               border: "1.5px solid #1190CA",

//                               backgroundColor: "#EAF3FB",
//                             }}
//                           >
//                             <td
//                               colSpan="6"
//                               style={{
//                                 padding: "8px 10px",

//                                 fontWeight: 600,

//                                 textAlign: "center",
//                               }}
//                             >
//                               Vendor Details
//                             </td>
//                           </tr>

//                           {/* Column Headers */}

//                           <tr style={{ border: "1.5px solid #1190CA" }}>
//                             <td
//                               style={{
//                                 padding: "6px 8px",

//                                 fontWeight: 500,

//                                 backgroundColor: "#1190CA",

//                                 color: "white",
//                               }}
//                             >
//                               Vendor Name
//                             </td>

//                             <td
//                               style={{
//                                 padding: "6px 8px",

//                                 fontWeight: 500,

//                                 backgroundColor: "#1190CA",

//                                 color: "white",
//                               }}
//                             >
//                               IFSC Code
//                             </td>

//                             <td
//                               style={{
//                                 padding: "6px 8px",

//                                 fontWeight: 500,

//                                 backgroundColor: "#1190CA",

//                                 color: "white",
//                               }}
//                             >
//                               Branch Name
//                             </td>

//                             <td
//                               style={{
//                                 padding: "6px 8px",
//                                 fontWeight: 500,
//                                 backgroundColor: "#1190CA",
//                                 color: "white",
//                               }}
//                             >
//                               Bank Name
//                             </td>

//                             <td
//                               style={{
//                                 padding: "6px 8px",

//                                 fontWeight: 500,

//                                 backgroundColor: "#1190CA",

//                                 color: "white",
//                               }}
//                             >
//                               Account Number
//                             </td>

//                             <td
//                               style={{
//                                 padding: "6px 8px",

//                                 fontWeight: 500,

//                                 backgroundColor: "#1190CA",

//                                 color: "white",
//                               }}
//                             >
//                               Amount{" "}
//                             </td>
//                           </tr>

//                           {/* {fetchSpecificDemandDeductionDetail &&
//                             fetchSpecificDemandDeductionDetail

//                               .filter(
//                                 (val) =>
//                                   val.TypeOfAmount == "Total Amount Payable",
//                               )

//                               .map((val, index) => { */}
//                               {uniqueVendorRows.map((val, index) => {
//                                 return (
//                                   <>
//                                     <tr
//                                       style={{ border: "1.5px solid #1190CA" }}
//                                       key={index}
//                                     >
//                                       <td
//                                         style={{
//                                           padding: "6px 8px",

//                                           backgroundColor: "white",
//                                         }}
//                                       >
//                                         {val.Vendor_Company_Name}
//                                       </td>

//                                       <td
//                                         style={{
//                                           padding: "6px 8px",

//                                           backgroundColor: "white",
//                                         }}
//                                       >
//                                         {val.Bank_IFSC_Code}
//                                       </td>

//                                       <td
//                                         style={{
//                                           padding: "6px 8px",

//                                           backgroundColor: "white",
//                                         }}
//                                       >
//                                         {val.Branch_Name}
//                                       </td>

//                                       <td
//                                         style={{
//                                           padding: "6px 8px",
//                                           backgroundColor: "white",
//                                         }}
//                                       >
//                                         {val.BANK_NAME}
//                                       </td>

//                                       <td
//                                         style={{
//                                           padding: "6px 8px",

//                                           backgroundColor: "white",
//                                         }}
//                                       >
//                                         {val.Bank_Account_Number}
//                                       </td>

//                                       <td
//                                         style={{
//                                           padding: "6px 8px",

//                                           backgroundColor: "white",
//                                         }}
//                                       >
//                                         {val.TotalAmount}
//                                       </td>
//                                     </tr>
//                                   </>
//                                 );
//                               })}

//                           <tr style={{ border: "1.5px solid #1190CA" }}>
//                             <td
//                               style={{
//                                 padding: "6px 8px",

//                                 backgroundColor: "#EAF3FB",

//                                 fontWeight: 500,
//                               }}
//                               colSpan={5}
//                             >
//                               Total
//                             </td>

//                             <td className="border border-dark">
//                               {selectedBillData.NetPayableAmount}
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>

//                       <table
//                         className="my-2"
//                         style={{ width: "100%", borderCollapse: "collapse" }}
//                       >
//                         <thead>
//                           <tr
//                             style={{
//                               border: "1.5px solid #1190CA",

//                               backgroundColor: "#EAF3FB",
//                             }}
//                           >
//                             <td
//                               colSpan="6"
//                               style={{
//                                 padding: "8px 10px",

//                                 fontWeight: 600,

//                                 textAlign: "center",
//                               }}
//                             >
//                               Deduction Amount Details
//                             </td>
//                           </tr>

//                           <tr
//                             style={{
//                               color: "white",

//                               border: "none",

//                               height: "3rem",
//                             }}
//                           >
//                             <th style={{ minWidth: "60px", padding: "8px" }}>
//                               Sr No
//                             </th>

//                             <th style={{ minWidth: "60px", padding: "8px" }}>
//                               Deduction Type
//                             </th>

//                             <th style={{ minWidth: "60px", padding: "8px" }}>
//                               Deduction Amount{" "}
//                             </th>

//                             {/* <th style={{ minWidth: "60px", padding: "8px" }}>

//                           Vendor Name

//                         </th> */}

//                             <th style={{ minWidth: "60px", padding: "8px" }}>
//                               Account Number
//                             </th>

//                             <th style={{ minWidth: "60px", padding: "8px" }}>
//                               Bank Name
//                             </th>

//                             <th style={{ minWidth: "60px", padding: "8px" }}>
//                               IFSC
//                             </th>
//                           </tr>
//                         </thead>

//                         <tbody>
//                           {/* {fetchSpecificDemandDeductionDetail.filter(
//                             (val) => val.TypeOfAmount != "Total Amount Payable",
//                           )?.length == 0 ? ( */}
//                           {uniqueDeductionRows.length == 0 ? (
//                             <tr>
//                               <td
//                                 colSpan="30"
//                                 style={{
//                                   textAlign: "center",

//                                   padding: "20px",

//                                   backgroundColor: "#f9f9f9",
//                                 }}
//                               >
//                                 No data available
//                               </td>
//                             </tr>
//                           ) : (
//                             // fetchSpecificDemandDeductionDetail

//                             //   .filter(
//                             //     (val) =>
//                             //       val.TypeOfAmount != "Total Amount Payable",
//                             //   )

//                             //   .map((technicalInwards, index) => {
//                               uniqueDeductionRows.map((technicalInwards, index) => {
//                                 const bgColor =
//                                   index % 2 === 0 ? "#E5F4FB" : "#CFE4F1";

//                                 return (
//                                   <tr
//                                     className="px-3"
//                                     key={index}
//                                     style={{
//                                       borderBottom: "1px solid #3b409e",

//                                       backgroundColor: bgColor,
//                                       fontSize: "18px",
//                                     }}
//                                   >
//                                     <td
//                                       style={{
//                                         fontSize: "13px",
//                                         padding: "5px",
//                                       }}
//                                     >
//                                       {index + 1}
//                                     </td>

//                                     <td
//                                       style={{
//                                         fontSize: "13px",
//                                         padding: "5px",
//                                       }}
//                                     >
//                                       {technicalInwards.TypeOfAmount}
//                                     </td>

//                                     <td
//                                       style={{
//                                         fontSize: "13px",
//                                         padding: "5px",
//                                       }}
//                                     >
//                                       {technicalInwards.TotalAmount}
//                                     </td>

//                                     {/* <td

//                                   style={{ fontSize: "12px", padding: "5px" }}

//                                 >

//                                   {technicalInwards.Vendor_Company_Name}

//                                 </td> */}

//                                     <td
//                                       style={{
//                                         fontSize: "13px",
//                                         padding: "5px",
//                                       }}
//                                     >
//                                       {technicalInwards.Bank_Account_Number}
//                                     </td>

//                                     <td
//                                       style={{
//                                         fontSize: "13px",
//                                         padding: "5px",
//                                       }}
//                                     >
//                                       {technicalInwards.Bank_Name}
//                                     </td>

//                                     <td
//                                       style={{
//                                         fontSize: "13px",
//                                         padding: "5px",
//                                       }}
//                                     >
//                                       {technicalInwards.Bank_IFSC_Code}
//                                     </td>
//                                   </tr>
//                                 );
//                               })
//                           )}

//                           <tr
//                             style={{
//                               border: "1.5px solid #1190CA",

//                               fontWeight: 600,

//                               background: "white",
//                             }}
//                           >
//                             <td
//                               colSpan="2"
//                               style={{ padding: "6px 8px", textAlign: "right" }}
//                             >
//                               Total
//                             </td>

//                             <td style={{ padding: "6px 8px" }}>
//                               {selectedBillData.DeductionAmount}
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>

//                       {/* Signature grid */}

//                       <table style={footerTitleStyle}>
//                         <tbody>
//                           <tr>
//                             <td style={footerTitleTdStyle}>मागील देयक तपशील</td>
//                           </tr>
//                         </tbody>
//                       </table>

//                       <table style={remarksTableStyle}>
//                         <tbody>
//                           <tr>
//                             <td style={remarksTableTdStyle}>
//                               प्रमाणित करण्यात येते की मा देयकाद्वारे आहारीत
//                               करण्यात येत असलेली संपूर्ण रक्कम याच जिल्हात खर्च
//                               करण्यात येणार आहे.
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>

//                       <table style={remarksTableStyle}>
//                         <tbody>
//                           <tr>
//                             <td style={remarksTableTdStyle}>
//                               स्वाक्षरी आहरण व संवितरण अधिकारी
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>

//                       <table style={instructionTableStyle}>
//                         <tbody>
//                           <tr>
//                             <td style={instructionTableTdStyle}>
//                               दुबार देयक प्रतिबंध नोंदवही तपासणीच्या आधारे
//                               प्रमाणित करण्यात येते की या देयकात नमूद केलेल्या
//                               अधिकाऱ्याचे कर्मचाऱ्याचे / पुरवठादाराचे तसेच त्यात
//                               नमूद केलेल्या बाबीचे / कालावधीचे देयक यापूर्वी
//                               ग्रामविकास विभाग मुबई येथे सादर करण्यात आले
//                               नव्हते. प्रमाणित करण्यात येते की वरील मागणी ही
//                               महाराष्ट्र कोषागार नियम / मुंबई वित्तीय नियम /
//                               महाराष्ट्र आकस्मिक खर्च नियम व वेळोवेळी विहित
//                               केलेल्या आदेशांच्या आधारे तयार करण्यात आली आहे.
//                               श्री. किंवा श्रीमती ______________________________
//                               यांना सदर देयकाची रक्कम/किंवा अपेक्षित देयक प्रदान
//                               करीता प्राधिकृत करण्यात आले असून त्यांची स्वाक्षरी
//                               खालीलप्रमाणे आहे.
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </>
//         )}

//         {showProgressModal && (
//           <div
//             className="modal d-block"
//             tabIndex="-1"
//             style={{ background: "rgba(0,0,0,0.5)" }}
//           >
//             <div className="modal-dialog modal-lg modal-dialog-centered">
//               <div className="modal-content">
//                 <div className="modal-header">
//                   <h5 className="modal-title">Physical Progress Images</h5>

//                   <button
//                     type="button"
//                     className="btn-close"
//                     onClick={() => setShowProgressModal(false)}
//                   ></button>
//                 </div>

//                 <div className="modal-body">
//                   <div className="row">
//                     {progressData?.length > 0 ? (
//                       progressData.map((item, index) => (
//                         <div className="col-md-4 mb-3" key={index}>
//                           <div
//                             style={{
//                               border: "1px solid #ddd",
//                               borderRadius: "10px",
//                               padding: "10px",
//                               background: "#fff",
//                             }}
//                           >
//                             {/* <img
//                                     src={`${apiurl}/UploadProgressDocsImages/${item.image}`}
//                                     alt="progress"
//                                     className="img-fluid rounded"
//                                     style={{
//                                       width: "100%",
//                                       maxHeight: "250px",
//                                       objectFit: "contain",
//                                     }}
//                                   /> */}
//                             <img
//                               src={`${apiurl}/UploadProgressDocsImages/${item?.image}`}
//                               alt="Document"
//                               style={{
//                                 width: "120px",
//                                 cursor: "pointer",
//                                 maxHeight: "250px",
//                                 objectFit: "contain",
//                               }}
//                               onClick={() => {
//                                 setSelectedImageIndex(index);
//                                 setSelectedImage(
//                                   `${apiurl}/UploadProgressDocsImages/${item.image}`,
//                                 );
//                                 setShowImageModal(true);
//                               }}
//                             />
//                             <div
//                               style={{
//                                 marginTop: "10px",
//                                 fontSize: "14px",
//                                 lineHeight: "1.7",
//                               }}
//                             >
//                               <div>
//                                 📍 <strong>Latitude:</strong>{" "}
//                                 {item.location?.latitude || "-"}
//                               </div>

//                               <div>
//                                 📍 <strong>Longitude:</strong>{" "}
//                                 {item.location?.longitude || "-"}
//                               </div>

//                               <div>
//                                 📅 <strong>Date:</strong>{" "}
//                                 {item.location?.date || "-"}
//                               </div>

//                               <div>
//                                 🕒 <strong>Time:</strong>{" "}
//                                 {item.location?.time || "-"}
//                               </div>

//                               <div>
//                                 ⛰️ <strong>Elevation:</strong>{" "}
//                                 {item.location?.elevation || "-"} m
//                               </div>

//                               <div>
//                                 🎯 <strong>Accuracy:</strong>{" "}
//                                 {item.location?.accuracy || "-"} m
//                               </div>
//                             </div>
//                           </div>
//                         </div>
//                       ))
//                     ) : (
//                       <p>No Images Found</p>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {showImageModal && (
//           <div
//             onClick={() => setShowImageModal(false)}
//             style={{
//               position: "fixed",
//               top: 0,
//               left: 0,
//               width: "100%",
//               height: "100%",
//               backgroundColor: "rgba(0,0,0,0.8)",
//               display: "flex",
//               justifyContent: "center",
//               alignItems: "center",
//               zIndex: 9999,
//             }}
//           >
//             {/* Previous Button */}
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();

//                 const prevIndex =
//                   (selectedImageIndex - 1 + progressData.length) %
//                   progressData.length;

//                 setSelectedImageIndex(prevIndex);
//               }}
//               style={{
//                 position: "absolute",
//                 left: "20px",
//                 top: "50%",
//                 transform: "translateY(-50%)",
//                 fontSize: "30px",
//                 background: "rgba(0,0,0,0.5)",
//                 color: "#fff",
//                 border: "none",
//                 borderRadius: "50%",
//                 width: "50px",
//                 height: "50px",
//                 cursor: "pointer",
//                 zIndex: 10001,
//               }}
//             >
//               ❮
//             </button>

//             {/* Close Button */}
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 setShowImageModal(false);
//               }}
//               style={{
//                 position: "absolute",
//                 top: "20px",
//                 right: "30px",
//                 fontSize: "30px",
//                 color: "#fff",
//                 background: "transparent",
//                 border: "none",
//                 cursor: "pointer",
//                 zIndex: 10001,
//               }}
//             >
//               ×
//             </button>

//             {/* Current Image */}
//             <img
//               src={`${apiurl}/UploadProgressDocsImages/${progressData[selectedImageIndex]?.image}`}
//               alt="Preview"
//               onClick={(e) => e.stopPropagation()}
//               style={{
//                 maxWidth: "85%",
//                 maxHeight: "85%",
//                 borderRadius: "8px",
//                 objectFit: "contain",
//               }}
//             />

//             {/* Image Counter */}
//             <div
//               style={{
//                 position: "absolute",
//                 bottom: "20px",
//                 color: "#fff",
//                 fontSize: "16px",
//                 fontWeight: "bold",
//                 background: "rgba(0,0,0,0.5)",
//                 padding: "6px 12px",
//                 borderRadius: "20px",
//               }}
//             >
//               {selectedImageIndex + 1} / {progressData.length}
//             </div>

//             {/* Next Button */}
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();

//                 const nextIndex =
//                   (selectedImageIndex + 1) % progressData.length;

//                 setSelectedImageIndex(nextIndex);
//               }}
//               style={{
//                 position: "absolute",
//                 right: "20px",
//                 top: "50%",
//                 transform: "translateY(-50%)",
//                 fontSize: "30px",
//                 background: "rgba(0,0,0,0.5)",
//                 color: "#fff",
//                 border: "none",
//                 borderRadius: "50%",
//                 width: "50px",
//                 height: "50px",
//                 cursor: "pointer",
//                 zIndex: 10001,
//               }}
//             >
//               ❯
//             </button>
//           </div>
//         )}

//         {/* Old code */}
//         {/* {showImageModal && (
//   <div
//     onClick={() => setShowImageModal(false)}
//     style={{
//       position: "fixed",
//       top: 0,
//       left: 0,
//       width: "100%",
//       height: "100%",
//       backgroundColor: "rgba(0,0,0,0.8)",
//       display: "flex",
//       justifyContent: "center",
//       alignItems: "center",
//       zIndex: 9999,
//     }}
//   >
//     <img
//       src={selectedImage}
//       alt="Preview"
//       onClick={(e) => e.stopPropagation()}
//       style={{
//         maxWidth: "90%",
//         maxHeight: "90%",
//         borderRadius: "8px",
//       }}
//     />
 
//     <button
//       onClick={() => setShowImageModal(false)}
//       style={{
//         position: "absolute",
//         top: "20px",
//         right: "30px",
//         fontSize: "30px",
//         color: "#fff",
//         background: "transparent",
//         border: "none",
//         cursor: "pointer",
//       }}
//     >
//       ×
//     </button>
//   </div>
// )} */}

//         {loading && (
//           <div
//             style={{
//               position: "fixed",
//               top: 0,
//               left: 0,
//               width: "100%",
//               height: "100%",
//               background: "rgba(255,255,255,0.4)",
//               backdropFilter: "blur(2px)",
//               display: "flex",
//               justifyContent: "center",
//               alignItems: "center",
//               zIndex: 9999,
//             }}
//           >
//             <div className="Adminloader"></div>
//           </div>
//         )}

//                 {/* ///Added New for demand id */}
//         {selectedDemandID && showspecific === 5 && (
//           <div
//             className="col-12"
//             style={{
//               backgroundColor: "#EFF3F8",
//               height: "90vh",
//               overflowX: "hidden",
//               overflowY: "scroll",
//             }}
//           >
//             <div className="col-11 m-auto">
//               <div className="col-12">
//                 <div className="col-12 py-3">
//                   <button
//                     className="btn btn-danger px-3 rounded-4 mb-3 mt-3"
//                     onClick={() => setShowSpecific(0)}
//                   >
//                     {t("Back")}
//                   </button>
//                 </div>

//                 <div className="col-11 my-4 mx-auto">
//                   <div className="row">
//                     <div className="col-9 my-auto">
//                       <h2
//                         className="col-10 m-0"
//                         style={{
//                           color: "#2C78DB",
//                           fontWeight: "700",
//                           fontSize: "1.4rem",
//                         }}
//                       >
//                         {t("Deduction Data of Bill")} {selectedDemandID}
//                       </h2>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="col-12 pb-5">
//                   <div
//                     className="m-auto mb-3"
//                     style={{
//                       backgroundColor: "white",
//                       padding: "40px 40px",
//                       boxShadow: "0px 0px 5px 1px #E4E8EC",
//                       borderRadius: "20px",
//                       width: "100%",
//                       maxWidth: "100%",
//                       overflowX: "hidden",
//                     }}
//                   >
//                     <div
//                       className="col-12 tableDesign"
//                       style={{ background: "#2C78DB", overflowX: "hidden" }}
//                     >
//                       <table
//                         className="m-0 p-0"
//                         style={{ width: "100%", borderCollapse: "collapse" }}
//                       >
//                         <thead>
//                           <tr
//                             style={{
//                               color: "white",
//                               border: "none",
//                               height: "3rem",
//                               fontSize: "18px",
//                             }}
//                           >
//                             <th style={{ width: "10%" }}>{t("Sr No")}</th>
//                             <th style={{ width: "45%" }}>
//                               {t("Deduction Name")}
//                             </th>
//                             <th style={{ width: "45%", textAlign: "left" }}>
//                               {t("Deduction Amount")}
//                             </th>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {fetchSpecificDemandDeductionDetail &&
//                             fetchSpecificDemandDeductionDetail.length > 0 ? (
//                             <>
//                               {fetchSpecificDemandDeductionDetail
//                                 .filter(
//                                   (item) =>
//                                     item.TypeOfAmount !== "Total Amount Payable",
//                                 )
//                                 .map((val, index) => {
//                                   const bgColor =
//                                     index % 2 === 0 ? "#E5F4FB" : "#CFE4F1";
//                                   return (
//                                     <tr
//                                       key={index}
//                                       style={{
//                                         backgroundColor: bgColor,
//                                         fontSize: "18px",
//                                         fontWeight: "400",
//                                       }}
//                                     >
//                                       <td>{index + 1}</td>
//                                       <td>{val.TypeOfAmount}</td>
//                                       <td style={{ textAlign: "left" }}>
//                                         ₹{" "}
//                                         {Number(val.TotalAmount).toLocaleString("en-IN")}
//                                       </td>
//                                     </tr>
//                                   );
//                                 })}

//                               <tr
//                                 style={{
//                                   fontWeight: "bold",
//                                   backgroundColor: "#f0f0f0",
//                                   fontSize: "18px"
//                                 }}
//                               >
//                                 <td colSpan={2}>{t("Total Deduction Amount")}</td>
//                                 <td style={{ textAlign: "left" }}>
//                                   ₹{" "}
//                                   {
//                                     Number(
//                                       fetchSpecificDemandDeductionDetail
//                                         .filter(
//                                           (item) =>
//                                             item.TypeOfAmount !==
//                                             "Total Amount Payable",
//                                         )
//                                         .reduce(
//                                           (acc, curr) =>
//                                             acc +
//                                             parseFloat(curr.TotalAmount || 0),
//                                           0,
//                                         ),
//                                     ).toLocaleString("en-IN")
//                                     // .toFixed(2)
//                                   }
//                                 </td>
//                               </tr>
//                             </>
//                           ) : (
//                             <tr>
//                               <td colSpan={5} className="fw-bold">
//                                 {t("No Data Found !!")}
//                               </td>
//                             </tr>
//                           )}
//                         </tbody>
//                       </table>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//         {/*/////////  */}
        
//       </div>
//     </>
//   );
// };

// export default ViewDemandGeneration;
